/**
 * Elite Batch 3 Part 2 — Workflow Templates 173–204
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

// =============================================================================
// ELITE BATCH 3 PART 2 — TEMPLATES 173–204
// =============================================================================

export const ELITE_BATCH3_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 173. AI CONTENT SUMMARIZER (7 nodes)
  // ===================================================================

  "AI Content Summarizer": {
    summary:
      "Takes a URL or raw text, extracts content, detects structure, generates a multi-format AI summary (TL;DR, key takeaways, detailed), routes to the right destination (Notion, Slack, or email), and handles extraction errors gracefully.",
    nodes: [
      trigger("Webhook: Content summarization request", "webhook_app", {
        source: "Slack Command / Browser Extension / API",
        event: "summarize.requested",
        howItStarts:
          "A user submits a URL or raw text block for summarization via Slack command, browser extension, or direct API call",
        setup:
          "Connect your Slack slash-command, browser extension, or internal tool to POST content (URL or text body) plus delivery preference (notion, slack, email) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Extract URL/text and delivery preference
      aiNode(
        "2",
        "AI extract content and preferences",
        280,
        "Read this summarization request payload. Find the content URL or raw text, delivery destination preference, and any length/format instructions regardless of field naming. Respond JSON: {content_url: string, raw_text: string, delivery_target: string, format_instructions: string, requester_email: string}",
        "{webhook_payload}",
        [
          { field: "content_url", as: "content_url", type: "string" },
          { field: "raw_text", as: "raw_text", type: "string" },
          { field: "delivery_target", as: "delivery_target", type: "string" },
          {
            field: "format_instructions",
            as: "format_instructions",
            type: "string",
          },
          { field: "requester_email", as: "requester_email", type: "string" },
        ],
      ),

      // 3. Extract content and detect structure
      n("3", "runCode", "Extract content and detect structure", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Extract content from URL or use raw text, detect structure (article, listicle, academic, social thread)
const url = inputs.content_url;
const raw = inputs.raw_text;
let content = raw || '';
let source_type = 'raw_text';
let word_count = 0;
let structure_type = 'general';

if (url && url.startsWith('http')) {
  // In production, fetch and parse the URL content
  source_type = 'url';
  content = raw || '[fetched content from ' + url + ']';
}

word_count = content.split(/\\s+/).filter(Boolean).length;

// Detect structure
if (content.match(/^\\d+\\./m)) structure_type = 'listicle';
else if (content.match(/abstract|methodology|conclusion/i)) structure_type = 'academic';
else if (content.match(/thread|tweet|post \\d/i)) structure_type = 'social_thread';
else if (word_count > 1000) structure_type = 'long_article';
else structure_type = 'short_article';

return { extracted_content: content, source_type, word_count, structure_type, extraction_success: content.length > 50 ? 'true' : 'false' };`,
          inputVariables: [
            { name: "content_url", type: "string" },
            { name: "raw_text", type: "string" },
          ],
          outputVariables: [
            { name: "extracted_content", type: "string" },
            { name: "source_type", type: "string" },
            { name: "word_count", type: "number" },
            { name: "structure_type", type: "string" },
            { name: "extraction_success", type: "string" },
          ],
        },
      }),

      // 4. AI generate structured summary
      aiNode(
        "4",
        "AI generate structured summary",
        760,
        "You are an expert content summarizer. Given the content and its structure type, produce a comprehensive multi-format summary. Adapt your approach based on structure_type. Respond JSON: {tldr: string, key_takeaways: string[], detailed_summary: string, notable_quotes: string[], content_category: string, reading_time_minutes: number}",
        "Structure type: {structure_type}. Word count: {word_count}. Format instructions: {format_instructions}. Content: {extracted_content}",
        [
          { field: "tldr", as: "tldr", type: "string" },
          { field: "key_takeaways", as: "key_takeaways", type: "string" },
          { field: "detailed_summary", as: "detailed_summary", type: "string" },
          { field: "notable_quotes", as: "notable_quotes", type: "string" },
          { field: "content_category", as: "content_category", type: "string" },
          {
            field: "reading_time_minutes",
            as: "reading_time_minutes",
            type: "number",
          },
        ],
      ),

      // 5. Condition: route by delivery target
      n("5", "condition", "Route by delivery destination", 1000, {
        conditionDetails: {
          conditions: [
            { field: "delivery_target", operator: "contains", value: "slack" },
          ],
        },
      }),

      // 6a. Slack delivery (True branch)
      slackNode(
        "6",
        "Deliver summary to Slack",
        1240,
        "📄 *Content Summary*\\nCategory: {content_category} | ~{reading_time_minutes} min read\\n\\n*TL;DR:* {tldr}\\n\\n*Key Takeaways:*\\n{key_takeaways}\\n\\n*Detailed Summary:*\\n{detailed_summary}",
      ),

      // 6b. Email delivery (False branch — Notion or email fallback)
      emailNode(
        "7",
        "Deliver summary via email",
        1240,
        "{requester_email}",
        "Content Summary: {content_category}",
        "TL;DR: {tldr}\\n\\nKey Takeaways:\\n{key_takeaways}\\n\\nDetailed Summary:\\n{detailed_summary}\\n\\nNotable Quotes:\\n{notable_quotes}",
        500,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 174. AI LEAD RESEARCH ASSISTANT (9 nodes)
  // ===================================================================

  "AI Lead Research Assistant": {
    summary:
      "Triggers on lead assignment, enriches via Clearbit, scans news via SerpAPI, researches the professional profile, generates AI pain-point hypotheses and personalization hooks, assembles a research brief, delivers to Slack, and handles partial data gracefully.",
    nodes: [
      trigger("Webhook: Lead assigned for research", "webhook_app", {
        source: "CRM / Lead Router",
        event: "lead.assigned",
        howItStarts: "A lead is assigned to an SDR and needs pre-call research",
        setup:
          "Connect your CRM lead assignment webhook to POST the lead data (name, company, domain, email) to the workflow URL whenever a lead is routed to a rep.",
        inputFields: [],
      }),

      // 2. Clearbit company enrichment
      n("2", "hitApi", "Clearbit company enrichment", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={lead_domain}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          body: [],
          variables: [
            { name: "name", customName: "company_name", type: "string" },
            {
              name: "metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "category.industry",
              customName: "industry",
              type: "string",
            },
            {
              name: "metrics.annualRevenue",
              customName: "annual_revenue",
              type: "string",
            },
            { name: "tech", customName: "tech_stack", type: "string" },
            {
              name: "description",
              customName: "company_description",
              type: "string",
            },
          ],
        },
      }),

      // 3. SerpAPI recent news scan
      n("3", "hitApi", "SerpAPI news scan for lead company", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={company_name}+news&tbm=nws&num=5&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            { name: "news_results", customName: "recent_news", type: "string" },
          ],
        },
      }),

      // 4. AI professional profile research
      aiNode(
        "4",
        "AI research professional profile",
        760,
        "You are an expert B2B researcher. Given company data and news, build a professional profile for the lead contact. Infer role challenges, priorities, and decision-making context. Respond JSON: {role_analysis: string, likely_priorities: string[], decision_context: string, reporting_structure_guess: string}",
        "Lead: {lead_name}, Title: {lead_title}, Company: {company_name}, Industry: {industry}, Employees: {employee_count}, Revenue: {annual_revenue}, Tech: {tech_stack}, Description: {company_description}, News: {recent_news}",
        [
          { field: "role_analysis", as: "role_analysis", type: "string" },
          {
            field: "likely_priorities",
            as: "likely_priorities",
            type: "string",
          },
          { field: "decision_context", as: "decision_context", type: "string" },
          {
            field: "reporting_structure_guess",
            as: "reporting_structure",
            type: "string",
          },
        ],
      ),

      // 5. AI pain-point hypotheses
      aiNode(
        "5",
        "AI generate pain-point hypotheses",
        1000,
        "You are a sales intelligence analyst. Based on the company profile, industry, tech stack, and recent news, generate specific pain-point hypotheses this lead likely faces. Be concrete and actionable. Respond JSON: {pain_points: string[], confidence_scores: string[], evidence_sources: string[]}",
        "Company: {company_name}, Industry: {industry}, Employees: {employee_count}, Tech: {tech_stack}, Role: {role_analysis}, Priorities: {likely_priorities}, News: {recent_news}",
        [
          { field: "pain_points", as: "pain_points", type: "string" },
          { field: "confidence_scores", as: "pain_confidence", type: "string" },
          { field: "evidence_sources", as: "pain_evidence", type: "string" },
        ],
      ),

      // 6. AI personalization hook generation
      aiNode(
        "6",
        "AI generate personalization hooks",
        1240,
        "You are a sales copywriter. Given pain points, company context, and news, generate personalization hooks for outreach emails and calls. Each hook should reference a specific, verifiable fact. Respond JSON: {email_hooks: string[], call_openers: string[], linkedin_message_hooks: string[], reference_points: string[]}",
        "Pain points: {pain_points}, Company: {company_name}, News: {recent_news}, Role: {role_analysis}, Priorities: {likely_priorities}",
        [
          { field: "email_hooks", as: "email_hooks", type: "string" },
          { field: "call_openers", as: "call_openers", type: "string" },
          {
            field: "linkedin_message_hooks",
            as: "linkedin_hooks",
            type: "string",
          },
          { field: "reference_points", as: "reference_points", type: "string" },
        ],
      ),

      // 7. Assemble research brief
      n("7", "runCode", "Assemble research brief document", 1480, {
        codeDetails: {
          language: "javascript",
          code: `// Assemble a comprehensive research brief
const brief = {
  header: '# Lead Research Brief: ' + inputs.lead_name + ' @ ' + inputs.company_name,
  company_overview: '## Company\\n' + inputs.company_description + '\\nIndustry: ' + inputs.industry + ' | Employees: ' + inputs.employee_count + ' | Revenue: ' + inputs.annual_revenue,
  tech_stack: '## Tech Stack\\n' + inputs.tech_stack,
  role_analysis: '## Role Analysis\\n' + inputs.role_analysis + '\\nPriorities: ' + inputs.likely_priorities,
  pain_points: '## Pain Points\\n' + inputs.pain_points + '\\nConfidence: ' + inputs.pain_confidence,
  personalization: '## Personalization Hooks\\nEmail: ' + inputs.email_hooks + '\\nCall: ' + inputs.call_openers + '\\nLinkedIn: ' + inputs.linkedin_hooks,
  news: '## Recent News\\n' + inputs.recent_news,
  data_completeness: (inputs.company_name && inputs.industry && inputs.pain_points) ? 'complete' : 'partial'
};
const brief_text = Object.values(brief).filter(v => v !== 'partial' && v !== 'complete').join('\\n\\n');
return { research_brief: brief_text, data_completeness: brief.data_completeness };`,
          inputVariables: [
            { name: "lead_name", type: "string" },
            { name: "company_name", type: "string" },
            { name: "company_description", type: "string" },
            { name: "industry", type: "string" },
            { name: "employee_count", type: "number" },
            { name: "annual_revenue", type: "string" },
            { name: "tech_stack", type: "string" },
            { name: "role_analysis", type: "string" },
            { name: "likely_priorities", type: "string" },
            { name: "pain_points", type: "string" },
            { name: "pain_confidence", type: "string" },
            { name: "email_hooks", type: "string" },
            { name: "call_openers", type: "string" },
            { name: "linkedin_hooks", type: "string" },
            { name: "recent_news", type: "string" },
          ],
          outputVariables: [
            { name: "research_brief", type: "string" },
            { name: "data_completeness", type: "string" },
          ],
        },
      }),

      // 8. Condition: check for partial data
      n("8", "condition", "Check data completeness", 1720, {
        conditionDetails: {
          conditions: [
            {
              field: "data_completeness",
              operator: "equals",
              value: "complete",
            },
          ],
        },
      }),

      // 9. Slack delivery (both branches converge here, just different messages)
      slackNode(
        "9",
        "Deliver research brief to Slack",
        1960,
        "🔬 *Lead Research Brief Ready*\\nLead: {lead_name} @ {company_name}\\nData: {data_completeness}\\n\\n{research_brief}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"),
      e("8", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 175. AD-TO-LP MESSAGE MATCH CHECKER (7 nodes)
  // ===================================================================

  "Ad to LP Message Match Checker": {
    summary:
      "Takes an ad creative and landing page URL, extracts LP content, decomposes the ad, scores message match with AI, routes high-severity mismatches to pause campaigns, and logs the full report to Google Sheets.",
    nodes: [
      trigger("Webhook: Message match check request", "webhook_app", {
        source: "Ad Manager / QA Tool",
        event: "match_check.requested",
        howItStarts:
          "A marketer submits an ad creative plus landing page URL for message-match analysis",
        setup:
          "POST the ad copy/creative details and the corresponding landing page URL to the workflow URL. Include campaign ID for tracking.",
        inputFields: [],
      }),

      // 2. LP content extraction
      n("2", "runCode", "Extract landing page content", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Parse webhook payload and extract LP elements
const payload = JSON.parse(inputs.webhook_payload || '{}');
const lp_url = payload.landing_page_url || payload.lp_url || payload.url || '';
const ad_copy = payload.ad_copy || payload.ad_text || payload.creative_text || '';
const ad_headline = payload.ad_headline || payload.headline || '';
const campaign_id = payload.campaign_id || payload.campaign || 'unknown';

// In production, fetch and parse the LP
// Extract key LP elements: headline, subhead, CTA, body, offer
return {
  lp_url: lp_url,
  lp_headline: '[extracted LP headline]',
  lp_subheadline: '[extracted LP subheadline]',
  lp_cta_text: '[extracted CTA button text]',
  lp_body_text: '[extracted body copy]',
  lp_offer: '[extracted offer details]',
  ad_copy: ad_copy,
  ad_headline: ad_headline,
  campaign_id: campaign_id,
  extraction_success: lp_url ? 'true' : 'false'
};`,
          inputVariables: [{ name: "webhook_payload", type: "string" }],
          outputVariables: [
            { name: "lp_url", type: "string" },
            { name: "lp_headline", type: "string" },
            { name: "lp_subheadline", type: "string" },
            { name: "lp_cta_text", type: "string" },
            { name: "lp_body_text", type: "string" },
            { name: "lp_offer", type: "string" },
            { name: "ad_copy", type: "string" },
            { name: "ad_headline", type: "string" },
            { name: "campaign_id", type: "string" },
            { name: "extraction_success", type: "string" },
          ],
        },
      }),

      // 3. AI decompose ad elements
      aiNode(
        "3",
        "AI decompose ad creative elements",
        520,
        "You are an ad analysis expert. Decompose this ad into its core messaging elements: primary promise, value proposition, CTA, emotional hook, specific claims, and offer details. Respond JSON: {primary_promise: string, value_proposition: string, ad_cta: string, emotional_hook: string, specific_claims: string[], offer_details: string}",
        "Ad headline: {ad_headline}. Ad copy: {ad_copy}",
        [
          {
            field: "primary_promise",
            as: "ad_primary_promise",
            type: "string",
          },
          { field: "value_proposition", as: "ad_value_prop", type: "string" },
          { field: "ad_cta", as: "ad_cta", type: "string" },
          { field: "emotional_hook", as: "ad_emotional_hook", type: "string" },
          { field: "specific_claims", as: "ad_claims", type: "string" },
          { field: "offer_details", as: "ad_offer", type: "string" },
        ],
      ),

      // 4. AI score message match
      aiNode(
        "4",
        "AI score message match alignment",
        760,
        "You are a CRO specialist. Compare the ad elements against the landing page elements. Score each dimension (headline match, offer match, CTA continuity, emotional tone, claim support) on 1-10. Provide an overall score and severity. Respond JSON: {headline_match_score: number, offer_match_score: number, cta_continuity_score: number, tone_match_score: number, claim_support_score: number, overall_score: number, severity: string, mismatches: string[], recommendations: string[]}",
        "AD: Promise={ad_primary_promise}, Value Prop={ad_value_prop}, CTA={ad_cta}, Hook={ad_emotional_hook}, Claims={ad_claims}, Offer={ad_offer}. LP: Headline={lp_headline}, Sub={lp_subheadline}, CTA={lp_cta_text}, Body={lp_body_text}, Offer={lp_offer}",
        [
          { field: "overall_score", as: "match_overall_score", type: "number" },
          { field: "severity", as: "match_severity", type: "string" },
          { field: "mismatches", as: "match_mismatches", type: "string" },
          {
            field: "recommendations",
            as: "match_recommendations",
            type: "string",
          },
          {
            field: "headline_match_score",
            as: "headline_score",
            type: "number",
          },
          { field: "offer_match_score", as: "offer_score", type: "number" },
        ],
      ),

      // 5. Severity routing condition
      n("5", "condition", "Check mismatch severity", 1000, {
        conditionDetails: {
          conditions: [
            { field: "match_severity", operator: "equals", value: "critical" },
          ],
        },
      }),

      // 6. Slack alert for critical mismatch (True)
      slackNode(
        "6",
        "Slack alert: campaign hold recommendation",
        1240,
        "🚨 *Critical Message Mismatch Detected*\\nCampaign: {campaign_id}\\nOverall Score: {match_overall_score}/10\\nSeverity: {match_severity}\\n\\n*Mismatches:*\\n{match_mismatches}\\n\\n*Recommendation: PAUSE campaign until LP is updated*\\n{match_recommendations}",
      ),

      // 7. Log report to Google Sheets
      n(
        "7",
        "hitApi",
        "Log match report to Google Sheets",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/MessageMatchLog!A:J:append?valueInputOption=USER_ENTERED",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "values",
                value:
                  "[['{campaign_id}','{lp_url}','{match_overall_score}','{match_severity}','{headline_score}','{offer_score}','{match_mismatches}','{match_recommendations}']]",
              },
            ],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "sheets_logged",
                type: "number",
              },
            ],
          },
        },
        500,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 176. ANGLE MATRIX GENERATOR (7 nodes)
  // ===================================================================

  "Angle Matrix Generator": {
    summary:
      "Takes a campaign brief, models audience problems with AI, generates creative angles, scores them for impact and feasibility, formats into a matrix, publishes to Google Sheets, and notifies the creative team on Slack.",
    nodes: [
      trigger("Webhook: Campaign brief for angle generation", "webhook_app", {
        source: "Project Management / Creative Brief Tool",
        event: "brief.submitted",
        howItStarts:
          "A campaign brief is submitted and needs creative angle exploration",
        setup:
          "POST the campaign brief including target audience, product/service, goals, and constraints to the workflow URL.",
        inputFields: [],
      }),

      // 2. AI audience problem modeling
      aiNode(
        "2",
        "AI model audience problems",
        280,
        "You are a consumer psychology expert. Given this campaign brief, model the target audience's problems, desires, fears, and aspirations. Think deeply about their current state vs desired state. Respond JSON: {audience_segment: string, core_problems: string[], desires: string[], fears: string[], aspirations: string[], current_state: string, desired_state: string, emotional_triggers: string[]}",
        "{webhook_payload}",
        [
          { field: "audience_segment", as: "audience_segment", type: "string" },
          { field: "core_problems", as: "core_problems", type: "string" },
          { field: "desires", as: "audience_desires", type: "string" },
          { field: "fears", as: "audience_fears", type: "string" },
          { field: "aspirations", as: "audience_aspirations", type: "string" },
          {
            field: "emotional_triggers",
            as: "emotional_triggers",
            type: "string",
          },
        ],
      ),

      // 3. AI angle generation
      aiNode(
        "3",
        "AI generate creative angles",
        520,
        "You are a senior creative strategist. Given the audience psychology model, generate 12+ creative angles. Each angle should have a name, core message, emotional lever, and suggested format. Mix rational, emotional, social proof, urgency, and aspirational approaches. Respond JSON: {angles: Array<{name: string, core_message: string, emotional_lever: string, format_suggestion: string, target_emotion: string}>}",
        "Audience: {audience_segment}. Problems: {core_problems}. Desires: {audience_desires}. Fears: {audience_fears}. Aspirations: {audience_aspirations}. Triggers: {emotional_triggers}",
        [{ field: "angles", as: "generated_angles", type: "string" }],
      ),

      // 4. AI score angles
      aiNode(
        "4",
        "AI score and rank angles",
        760,
        "You are a performance marketing strategist. Score each creative angle on: originality (1-10), emotional impact (1-10), clarity (1-10), scalability (1-10), and differentiation (1-10). Calculate a weighted total. Respond JSON: {scored_angles: Array<{name: string, originality: number, emotional_impact: number, clarity: number, scalability: number, differentiation: number, total_score: number, rank: number}>, top_3_angles: string[], production_priority: string[]}",
        "Angles: {generated_angles}. Audience: {audience_segment}",
        [
          { field: "scored_angles", as: "scored_angles", type: "string" },
          { field: "top_3_angles", as: "top_angles", type: "string" },
          {
            field: "production_priority",
            as: "production_priority",
            type: "string",
          },
        ],
      ),

      // 5. Format angle matrix
      n("5", "runCode", "Format angle matrix for Sheets", 1000, {
        codeDetails: {
          language: "javascript",
          code: `// Format scored angles into a matrix structure for Google Sheets
const angles = JSON.parse(inputs.scored_angles || '[]');
const rows = angles.map(function(a) {
  return [a.name, a.originality, a.emotional_impact, a.clarity, a.scalability, a.differentiation, a.total_score, a.rank].join(',');
});
const header = 'Angle Name,Originality,Emotional Impact,Clarity,Scalability,Differentiation,Total Score,Rank';
const matrix_csv = header + '\\n' + rows.join('\\n');
const sheet_values = angles.map(function(a) {
  return [a.name, a.originality, a.emotional_impact, a.clarity, a.scalability, a.differentiation, a.total_score, a.rank];
});
return { matrix_csv: matrix_csv, sheet_values: JSON.stringify(sheet_values), angle_count: angles.length || 0 };`,
          inputVariables: [{ name: "scored_angles", type: "string" }],
          outputVariables: [
            { name: "matrix_csv", type: "string" },
            { name: "sheet_values", type: "string" },
            { name: "angle_count", type: "number" },
          ],
        },
      }),

      // 6. Publish to Google Sheets
      n("6", "hitApi", "Publish angle matrix to Google Sheets", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/AngleMatrix!A:H:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "values", value: "{sheet_values}" }],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_written",
              type: "number",
            },
          ],
        },
      }),

      // 7. Slack notification
      slackNode(
        "7",
        "Notify creative team on Slack",
        1480,
        "🎨 *Angle Matrix Ready*\\nAudience: {audience_segment}\\nAngles Generated: {angle_count}\\n\\n*Top 3 Angles:*\\n{top_angles}\\n\\n*Production Priority:*\\n{production_priority}\\n\\nFull matrix in Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 177. BACKLINK HUNTER (8 nodes)
  // ===================================================================

  "Backlink Hunter": {
    summary:
      "Takes a competitor domain, pulls backlinks from Ahrefs, filters for outreach-worthy opportunities, discovers contacts via Hunter.io, drafts AI outreach emails, validates emails with ZeroBounce, queues outreach with tracking, and sends a weekly summary report.",
    nodes: [
      trigger(
        "Webhook: Competitor domain for backlink hunting",
        "webhook_app",
        {
          source: "SEO Tool / Manual Submission",
          event: "backlink_hunt.requested",
          howItStarts:
            "An SEO team member submits a competitor domain to find backlink acquisition opportunities",
          setup:
            "POST the competitor domain, your own domain (for comparison), and any target page URLs to the workflow URL.",
          inputFields: [],
        },
      ),

      // 2. Ahrefs backlink pull
      n("2", "hitApi", "Ahrefs pull competitor backlinks", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://apiv2.ahrefs.com?token={ahrefs_key}&from=backlinks&target={competitor_domain}&mode=domain&limit=50&order_by=domain_rating:desc&output=json",
          headers: [],
          body: [],
          variables: [
            { name: "refpages", customName: "backlink_list", type: "string" },
            {
              name: "stats.total",
              customName: "total_backlinks",
              type: "number",
            },
          ],
        },
      }),

      // 3. Filter and score opportunities
      n("3", "runCode", "Filter and rank backlink opportunities", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Filter backlinks for outreach-worthy opportunities
const backlinks = JSON.parse(inputs.backlink_list || '[]');
const own_domain = inputs.own_domain || '';

// Filter: DR > 30, not already linking to us, dofollow preferred
const opportunities = backlinks
  .filter(function(bl) {
    return bl.domain_rating > 30 && !bl.url_from.includes(own_domain);
  })
  .map(function(bl) {
    return {
      source_url: bl.url_from,
      source_domain: bl.domain_from || bl.url_from.split('/')[2],
      domain_rating: bl.domain_rating,
      link_type: bl.type || 'dofollow',
      anchor_text: bl.anchor || '',
      outreach_priority: bl.domain_rating > 60 ? 'high' : bl.domain_rating > 40 ? 'medium' : 'low'
    };
  })
  .slice(0, 20);

const domains_for_hunter = opportunities.map(function(o) { return o.source_domain; }).join(',');
return { filtered_opportunities: JSON.stringify(opportunities), opportunity_count: opportunities.length, domains_for_hunter: domains_for_hunter, high_priority_count: opportunities.filter(function(o) { return o.outreach_priority === 'high'; }).length };`,
          inputVariables: [
            { name: "backlink_list", type: "string" },
            { name: "own_domain", type: "string" },
          ],
          outputVariables: [
            { name: "filtered_opportunities", type: "string" },
            { name: "opportunity_count", type: "number" },
            { name: "domains_for_hunter", type: "string" },
            { name: "high_priority_count", type: "number" },
          ],
        },
      }),

      // 4. Hunter.io contact discovery
      n("4", "hitApi", "Hunter.io discover outreach contacts", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/domain-search?domain={domains_for_hunter}&api_key={hunter_key}&type=personal",
          headers: [],
          body: [],
          variables: [
            {
              name: "data.emails",
              customName: "discovered_contacts",
              type: "string",
            },
            {
              name: "meta.results",
              customName: "contacts_found",
              type: "number",
            },
          ],
        },
      }),

      // 5. AI draft outreach emails
      aiNode(
        "5",
        "AI draft personalized outreach emails",
        1000,
        "You are an expert link-building outreach specialist. For each backlink opportunity and its contact, draft a personalized outreach email. Reference the specific page, explain your content's value, and make the ask natural. Respond JSON: {outreach_drafts: Array<{domain: string, contact_email: string, subject: string, body: string, personalization_hook: string}>, batch_count: number}",
        "Opportunities: {filtered_opportunities}. Contacts: {discovered_contacts}. Our domain: {own_domain}",
        [
          { field: "outreach_drafts", as: "outreach_drafts", type: "string" },
          { field: "batch_count", as: "draft_count", type: "number" },
        ],
      ),

      // 6. ZeroBounce email validation
      n("6", "hitApi", "ZeroBounce validate outreach emails", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={contact_email}",
          headers: [],
          body: [],
          variables: [
            {
              name: "status",
              customName: "email_validation_status",
              type: "string",
            },
            {
              name: "sub_status",
              customName: "email_sub_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. Queue outreach with tracking
      n("7", "hitApi", "Queue outreach emails with tracking", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.sendgrid.com/v3/mail/send",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "personalizations",
              value: '[{"to":[{"email":"{contact_email}"}]}]',
            },
            {
              key: "from",
              value: '{"email":"{sender_email}","name":"{sender_name}"}',
            },
            { key: "subject", value: "Backlink opportunity" },
            {
              key: "content",
              value: '[{"type":"text/html","value":"{outreach_drafts}"}]',
            },
            {
              key: "tracking_settings",
              value:
                '{"open_tracking":{"enable":true},"click_tracking":{"enable":true}}',
            },
          ],
          variables: [
            { name: "statusCode", customName: "send_status", type: "number" },
          ],
        },
      }),

      // 8. Weekly report email
      emailNode(
        "8",
        "Send weekly backlink hunt report",
        1720,
        "{seo_team_email}",
        "Backlink Hunt Report: {competitor_domain}",
        "Backlink Hunt Summary:\\n\\nCompetitor: {competitor_domain}\\nTotal Backlinks Found: {total_backlinks}\\nFiltered Opportunities: {opportunity_count}\\nHigh Priority: {high_priority_count}\\nContacts Discovered: {contacts_found}\\nOutreach Drafts Sent: {draft_count}\\n\\nCheck your outreach queue for tracking updates.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 178. CHANNEL CAPTION REWRITER (7 nodes)
  // ===================================================================

  "Channel Caption Rewriter": {
    summary:
      "Takes content and extracts the core message, generates platform-specific rewrites (LinkedIn, X, Instagram, TikTok), adds platform-appropriate hashtags and emoji, QA-checks cross-version consistency, pushes to content calendar, and alerts the team.",
    nodes: [
      trigger("Webhook: Content for multi-platform rewrite", "webhook_app", {
        source: "Content Calendar / CMS",
        event: "content.ready_for_distribution",
        howItStarts:
          "A piece of content is approved and needs platform-specific caption variants",
        setup:
          "POST the original content, content type, target platforms, and any brand guidelines to the workflow URL.",
        inputFields: [],
      }),

      // 2. AI extract core message
      aiNode(
        "2",
        "AI extract core message",
        280,
        "You are a content strategist. Read this content and extract the core message, key value proposition, target audience, desired action, and tone. This will be the foundation for platform-specific rewrites. Respond JSON: {core_message: string, value_proposition: string, target_audience: string, desired_action: string, tone: string, key_facts: string[], content_type: string}",
        "{webhook_payload}",
        [
          { field: "core_message", as: "core_message", type: "string" },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "desired_action", as: "desired_action", type: "string" },
          { field: "tone", as: "brand_tone", type: "string" },
          { field: "key_facts", as: "key_facts", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
        ],
      ),

      // 3. AI platform-specific rewrites
      aiNode(
        "3",
        "AI generate platform-specific captions",
        520,
        "You are a social media expert. Rewrite the core message for each platform following their best practices. LinkedIn: professional, paragraph-style, 1300 chars. X/Twitter: punchy, under 280 chars. Instagram: storytelling, line breaks, 2200 chars max. TikTok: casual, hook-first, under 300 chars. Facebook: conversational, medium length. Respond JSON: {linkedin: string, twitter: string, instagram: string, tiktok: string, facebook: string}",
        "Core message: {core_message}. Value prop: {value_proposition}. Audience: {target_audience}. Action: {desired_action}. Tone: {brand_tone}. Facts: {key_facts}",
        [
          { field: "linkedin", as: "caption_linkedin", type: "string" },
          { field: "twitter", as: "caption_twitter", type: "string" },
          { field: "instagram", as: "caption_instagram", type: "string" },
          { field: "tiktok", as: "caption_tiktok", type: "string" },
          { field: "facebook", as: "caption_facebook", type: "string" },
        ],
      ),

      // 4. AI add platform elements (hashtags, emoji, CTAs)
      aiNode(
        "4",
        "AI add hashtags, emoji, and CTAs",
        760,
        "You are a social media optimization specialist. For each platform caption, add appropriate hashtags (research-based, mix of popular and niche), emoji placement (platform-appropriate density), and CTAs. Respond JSON: {linkedin_final: string, twitter_final: string, instagram_final: string, tiktok_final: string, facebook_final: string, hashtag_sets: {linkedin: string[], twitter: string[], instagram: string[]}}",
        "LinkedIn: {caption_linkedin}. Twitter: {caption_twitter}. Instagram: {caption_instagram}. TikTok: {caption_tiktok}. Facebook: {caption_facebook}. Content type: {content_type}. Industry: {target_audience}",
        [
          { field: "linkedin_final", as: "final_linkedin", type: "string" },
          { field: "twitter_final", as: "final_twitter", type: "string" },
          { field: "instagram_final", as: "final_instagram", type: "string" },
          { field: "tiktok_final", as: "final_tiktok", type: "string" },
          { field: "facebook_final", as: "final_facebook", type: "string" },
          { field: "hashtag_sets", as: "hashtag_sets", type: "string" },
        ],
      ),

      // 5. Cross-version QA condition
      n("5", "condition", "Cross-version QA: message consistency check", 1000, {
        conditionDetails: {
          conditions: [
            { field: "core_message", operator: "isNotEmpty", value: "" },
          ],
        },
      }),

      // 6. Push to content calendar (Google Sheets)
      n("6", "hitApi", "Push captions to content calendar", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/ContentCalendar!A:G:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                "[['{content_type}','{final_linkedin}','{final_twitter}','{final_instagram}','{final_tiktok}','{final_facebook}','{hashtag_sets}']]",
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "calendar_rows_added",
              type: "number",
            },
          ],
        },
      }),

      // 7. Slack review notification
      slackNode(
        "7",
        "Notify team: captions ready for review",
        1480,
        "📝 *Platform Captions Ready*\\nContent: {content_type}\\nCore Message: {core_message}\\n\\n*LinkedIn:* {final_linkedin}\\n*Twitter:* {final_twitter}\\n*Instagram:* (see Sheets)\\n*TikTok:* {final_tiktok}\\n\\nAll versions pushed to content calendar.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 179. COMMUNITY FAQ TO CONTENT PIPELINE (8 nodes)
  // ===================================================================

  "Community FAQ to Content Pipeline": {
    summary:
      "Scans community forums on a schedule, clusters and ranks questions with AI, generates content briefs, checks existing knowledge base for duplicates, auto-drafts FAQ articles, creates tasks in Asana, notifies the content team, and logs feedback for iteration.",
    nodes: [
      trigger("Scheduled: Community FAQ scan", "schedule", {
        schedule: "Every Monday at 9am",
        howItStarts:
          "Runs weekly to scan community forums and support channels for recurring questions",
        setup:
          "Configure the schedule and connect your community platform API credentials (Discourse, Circle, Slack channels, etc.).",
        inputFields: [],
      }),

      // 2. AI cluster and rank questions
      aiNode(
        "2",
        "AI cluster and rank community questions",
        280,
        "You are a community analytics expert. Analyze the recent community questions and discussions. Cluster them by topic, rank by frequency and engagement, and identify content gaps. Respond JSON: {clusters: Array<{topic: string, question_count: number, sample_questions: string[], urgency: string}>, top_topics: string[], total_questions_analyzed: number, content_gaps: string[]}",
        "Community data from the past 7 days. Source channels: support forum, Slack community, social mentions. Analyze all questions and identify patterns.",
        [
          { field: "clusters", as: "question_clusters", type: "string" },
          { field: "top_topics", as: "top_topics", type: "string" },
          {
            field: "total_questions_analyzed",
            as: "questions_analyzed",
            type: "number",
          },
          { field: "content_gaps", as: "content_gaps", type: "string" },
        ],
      ),

      // 3. AI generate content briefs
      aiNode(
        "3",
        "AI generate content briefs from FAQ clusters",
        520,
        "You are a content strategist. For each top FAQ cluster, generate a content brief that would definitively answer the question. Include title, target keyword, outline, word count target, and SEO notes. Respond JSON: {briefs: Array<{title: string, target_keyword: string, outline: string[], word_count_target: number, seo_notes: string, priority: string}>, total_briefs: number}",
        "Top topics: {top_topics}. Question clusters: {question_clusters}. Content gaps: {content_gaps}",
        [
          { field: "briefs", as: "content_briefs", type: "string" },
          { field: "total_briefs", as: "brief_count", type: "number" },
        ],
      ),

      // 4. Condition: check if KB article already exists
      n("4", "condition", "Check if existing KB covers this topic", 760, {
        conditionDetails: {
          conditions: [
            { field: "content_gaps", operator: "isNotEmpty", value: "" },
          ],
        },
      }),

      // 5. AI auto-draft FAQ articles
      aiNode(
        "5",
        "AI auto-draft FAQ articles",
        1000,
        "You are a technical writer. For each content brief, write a complete FAQ article draft. Use clear headings, step-by-step instructions where appropriate, and include code examples if relevant. Respond JSON: {drafts: Array<{title: string, body: string, meta_description: string, tags: string[]}>, drafts_completed: number}",
        "Briefs: {content_briefs}",
        [
          { field: "drafts", as: "faq_drafts", type: "string" },
          { field: "drafts_completed", as: "drafts_completed", type: "number" },
        ],
      ),

      // 6. Create Asana tasks
      n("6", "hitApi", "Create Asana tasks for content review", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Review FAQ Draft: {top_topics}","projects":["{asana_project_id}"],"notes":"Auto-generated FAQ drafts from community analysis. {drafts_completed} articles need review.","assignee":"{content_lead_email}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "asana_task_id", type: "string" },
          ],
        },
      }),

      // 7. Slack notification
      slackNode(
        "7",
        "Notify content team on Slack",
        1480,
        "📚 *Community FAQ Pipeline Update*\\nQuestions Analyzed: {questions_analyzed}\\nContent Briefs: {brief_count}\\nDrafts Completed: {drafts_completed}\\n\\n*Top Topics:*\\n{top_topics}\\n\\n*Content Gaps:*\\n{content_gaps}\\n\\nAsana task created for review: {asana_task_id}",
      ),

      // 8. Publication feedback loop logging
      n("8", "hitApi", "Log pipeline run to feedback tracker", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/FAQPipeline!A:E:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                "[['{questions_analyzed}','{brief_count}','{drafts_completed}','{top_topics}','{content_gaps}']]",
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "feedback_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 180. COMPETITOR EMAIL DIGEST ANALYZER (7 nodes)
  // ===================================================================

  "Competitor Email Digest Analyzer": {
    summary:
      "Ingests competitor marketing emails, extracts assets and structure, classifies strategically with AI, performs teardown analysis, compares against your own program, generates actionable recommendations, and delivers to the strategy team.",
    nodes: [
      trigger("Email: Competitor email forwarded for analysis", "email", {
        howItStarts:
          "A team member forwards a competitor marketing email to the analysis inbox",
        setup:
          "Set up an email forwarding rule: any email sent to compete-analysis@yourcompany.com gets POSTed to the workflow URL. Include raw HTML and headers.",
        inputFields: [],
      }),

      // 2. Extract email assets and structure
      n("2", "runCode", "Extract email assets and structure", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Parse the forwarded competitor email
const payload = inputs.webhook_payload || '';
// Extract key email components
const subject_match = payload.match(/Subject:\\s*(.+)/i);
const from_match = payload.match(/From:\\s*(.+)/i);

return {
  competitor_name: from_match ? from_match[1].replace(/<.*>/, '').trim() : 'Unknown',
  email_subject: subject_match ? subject_match[1].trim() : 'No subject',
  email_body_html: payload,
  has_images: payload.includes('<img') ? 'true' : 'false',
  has_cta_button: payload.match(/btn|button|cta/i) ? 'true' : 'false',
  link_count: (payload.match(/href=/gi) || []).length,
  word_count: payload.replace(/<[^>]*>/g, ' ').split(/\\s+/).filter(Boolean).length,
  send_date: new Date().toISOString().split('T')[0]
};`,
          inputVariables: [{ name: "webhook_payload", type: "string" }],
          outputVariables: [
            { name: "competitor_name", type: "string" },
            { name: "email_subject", type: "string" },
            { name: "email_body_html", type: "string" },
            { name: "has_images", type: "string" },
            { name: "has_cta_button", type: "string" },
            { name: "link_count", type: "number" },
            { name: "word_count", type: "number" },
            { name: "send_date", type: "string" },
          ],
        },
      }),

      // 3. AI strategic classification
      aiNode(
        "3",
        "AI classify email strategy",
        520,
        "You are an email marketing strategist. Classify this competitor email by type (promotional, nurture, onboarding, retention, win-back, announcement, newsletter), funnel stage, primary objective, and sophistication level. Respond JSON: {email_type: string, funnel_stage: string, primary_objective: string, sophistication_level: string, segmentation_signals: string[], personalization_level: string}",
        "Subject: {email_subject}. From: {competitor_name}. Has images: {has_images}. Has CTA: {has_cta_button}. Links: {link_count}. Body: {email_body_html}",
        [
          { field: "email_type", as: "email_type", type: "string" },
          { field: "funnel_stage", as: "funnel_stage", type: "string" },
          {
            field: "primary_objective",
            as: "primary_objective",
            type: "string",
          },
          {
            field: "sophistication_level",
            as: "sophistication_level",
            type: "string",
          },
          {
            field: "personalization_level",
            as: "personalization_level",
            type: "string",
          },
        ],
      ),

      // 4. AI teardown analysis
      aiNode(
        "4",
        "AI perform email teardown",
        760,
        "You are an email marketing analyst. Perform a detailed teardown: subject line effectiveness, preview text strategy, email structure, copy quality, CTA strength, design patterns, urgency/scarcity tactics, social proof usage, and mobile optimization signals. Respond JSON: {subject_analysis: string, copy_analysis: string, cta_analysis: string, design_patterns: string[], tactics_used: string[], strengths: string[], weaknesses: string[], innovative_elements: string[]}",
        "Subject: {email_subject}. Type: {email_type}. Stage: {funnel_stage}. Objective: {primary_objective}. Body: {email_body_html}",
        [
          { field: "subject_analysis", as: "subject_analysis", type: "string" },
          { field: "copy_analysis", as: "copy_analysis", type: "string" },
          { field: "cta_analysis", as: "cta_analysis", type: "string" },
          { field: "tactics_used", as: "tactics_used", type: "string" },
          { field: "strengths", as: "email_strengths", type: "string" },
          { field: "weaknesses", as: "email_weaknesses", type: "string" },
        ],
      ),

      // 5. AI compare with own program
      aiNode(
        "5",
        "AI compare with our email program",
        1000,
        "You are a competitive intelligence analyst. Compare this competitor email against standard email marketing best practices and identify what we can learn or adopt. Focus on actionable insights. Respond JSON: {comparison_summary: string, steal_worthy_tactics: string[], improvement_areas: string[], test_ideas: string[], priority_actions: string[]}",
        "Competitor: {competitor_name}. Type: {email_type}. Strengths: {email_strengths}. Weaknesses: {email_weaknesses}. Tactics: {tactics_used}. Subject: {subject_analysis}. CTA: {cta_analysis}",
        [
          {
            field: "comparison_summary",
            as: "comparison_summary",
            type: "string",
          },
          { field: "steal_worthy_tactics", as: "steal_worthy", type: "string" },
          { field: "test_ideas", as: "test_ideas", type: "string" },
          { field: "priority_actions", as: "priority_actions", type: "string" },
        ],
      ),

      // 6. AI generate recommendations
      aiNode(
        "6",
        "AI generate actionable recommendations",
        1240,
        "You are a senior email marketing director. Based on the full competitor analysis, create a prioritized recommendation report. Include quick wins (this week), medium-term improvements (this month), and strategic shifts (this quarter). Respond JSON: {quick_wins: string[], medium_term: string[], strategic_shifts: string[], executive_summary: string}",
        "Competitor: {competitor_name}. Comparison: {comparison_summary}. Steal-worthy: {steal_worthy}. Test ideas: {test_ideas}. Priority actions: {priority_actions}",
        [
          { field: "quick_wins", as: "quick_wins", type: "string" },
          { field: "medium_term", as: "medium_term", type: "string" },
          { field: "strategic_shifts", as: "strategic_shifts", type: "string" },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
        ],
      ),

      // 7. Deliver to strategy team (Slack + email)
      slackNode(
        "7",
        "Deliver analysis to strategy team",
        1480,
        "📧 *Competitor Email Teardown*\\nCompetitor: {competitor_name}\\nSubject: {email_subject}\\nType: {email_type} | Stage: {funnel_stage}\\n\\n*Executive Summary:*\\n{executive_summary}\\n\\n*Quick Wins:*\\n{quick_wins}\\n\\n*Steal-Worthy Tactics:*\\n{steal_worthy}\\n\\n*Test Ideas:*\\n{test_ideas}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 181. COMPETITOR SHADOW MONITOR (8 nodes)
  // ===================================================================

  "Competitor Shadow Monitor": {
    summary:
      "Runs daily to scrape competitor pricing pages, changelogs, and blogs, monitors news via SerpAPI, analyzes diffs and significance with AI, generates intel briefs, routes alerts by severity, and archives everything to Sheets.",
    nodes: [
      trigger("Scheduled: Daily competitor shadow scan", "schedule", {
        schedule: "Every day at 7am",
        howItStarts:
          "Runs daily to monitor competitor pricing, features, content, and news for changes",
        setup:
          "Configure target competitor domains, pricing page URLs, and changelog URLs. Set up SerpAPI and Google Sheets credentials.",
        inputFields: [],
      }),

      // 2. Scrape pricing pages
      n("2", "runCode", "Scrape competitor pricing pages", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Scrape and compare competitor pricing pages
const competitors = ['competitor1.com', 'competitor2.com', 'competitor3.com'];
const pricing_data = competitors.map(function(domain) {
  // In production, fetch each pricing page and extract plan/price data
  return {
    domain: domain,
    url: 'https://' + domain + '/pricing',
    plans_detected: 3,
    scraped_at: new Date().toISOString(),
    raw_content: '[pricing page content for ' + domain + ']'
  };
});
return {
  pricing_snapshots: JSON.stringify(pricing_data),
  competitors_scanned: competitors.length,
  scan_timestamp: new Date().toISOString()
};`,
          inputVariables: [],
          outputVariables: [
            { name: "pricing_snapshots", type: "string" },
            { name: "competitors_scanned", type: "number" },
            { name: "scan_timestamp", type: "string" },
          ],
        },
      }),

      // 3. Scrape changelogs and features
      n("3", "runCode", "Scrape competitor changelogs and features", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Scrape competitor changelog/release notes pages
const changelog_urls = [
  'https://competitor1.com/changelog',
  'https://competitor2.com/whats-new',
  'https://competitor3.com/releases'
];
const changelog_data = changelog_urls.map(function(url) {
  return { url: url, content: '[changelog content from ' + url + ']', scraped_at: new Date().toISOString() };
});
return {
  changelog_snapshots: JSON.stringify(changelog_data),
  changelogs_scanned: changelog_data.length
};`,
          inputVariables: [],
          outputVariables: [
            { name: "changelog_snapshots", type: "string" },
            { name: "changelogs_scanned", type: "number" },
          ],
        },
      }),

      // 4. SerpAPI blog/social/news watch
      n("4", "hitApi", "SerpAPI competitor news and social watch", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={competitor_names}+announcement+OR+launch+OR+update&tbm=nws&tbs=qdr:d&num=10&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "news_results",
              customName: "competitor_news",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "news_count",
              type: "number",
            },
          ],
        },
      }),

      // 5. AI diff + significance analysis
      aiNode(
        "5",
        "AI analyze diffs and significance",
        1000,
        "You are a competitive intelligence analyst. Compare today's competitor data against known baselines. Identify changes in pricing, new features, messaging shifts, and news developments. Score each change for business significance (1-10). Respond JSON: {pricing_changes: Array<{competitor: string, change: string, significance: number}>, feature_changes: Array<{competitor: string, feature: string, significance: number}>, news_developments: Array<{headline: string, competitor: string, significance: number}>, overall_alert_level: string, max_significance: number}",
        "Pricing snapshots: {pricing_snapshots}. Changelogs: {changelog_snapshots}. News: {competitor_news}",
        [
          { field: "pricing_changes", as: "pricing_changes", type: "string" },
          { field: "feature_changes", as: "feature_changes", type: "string" },
          {
            field: "news_developments",
            as: "news_developments",
            type: "string",
          },
          { field: "overall_alert_level", as: "alert_level", type: "string" },
          { field: "max_significance", as: "max_significance", type: "number" },
        ],
      ),

      // 6. AI generate intel brief
      aiNode(
        "6",
        "AI generate competitive intel brief",
        1240,
        "You are a VP of Strategy writing a daily competitive intel brief. Synthesize all changes into an executive-ready brief with impact assessment and recommended actions. Respond JSON: {brief_title: string, executive_summary: string, pricing_section: string, feature_section: string, news_section: string, recommended_actions: string[], competitive_threat_level: string}",
        "Alert level: {alert_level}. Pricing changes: {pricing_changes}. Feature changes: {feature_changes}. News: {news_developments}",
        [
          { field: "brief_title", as: "brief_title", type: "string" },
          { field: "executive_summary", as: "intel_summary", type: "string" },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
          {
            field: "competitive_threat_level",
            as: "threat_level",
            type: "string",
          },
        ],
      ),

      // 7. Alert routing condition
      n("7", "condition", "Route by alert severity: major vs digest", 1480, {
        conditionDetails: {
          conditions: [
            { field: "max_significance", operator: "greaterThan", value: "7" },
          ],
        },
      }),

      // 8. Historical archive to Sheets (both branches)
      n("8", "hitApi", "Archive intel to Google Sheets", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/CompetitorIntel!A:G:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                "[['{scan_timestamp}','{alert_level}','{threat_level}','{pricing_changes}','{feature_changes}','{news_developments}','{recommended_actions}']]",
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "archive_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 182. HOOK LIBRARY FROM COMPETITOR ADS (7 nodes)
  // ===================================================================

  "Hook Library from Competitor Ads": {
    summary:
      "Ingests competitor ad examples, extracts hooks with AI, categorizes by psychological lever, adapts hooks for your brand, scores and ranks them, publishes the library to Sheets, and alerts the creative team.",
    nodes: [
      trigger("Webhook: Competitor ad examples submitted", "webhook_app", {
        source: "Ad Spy Tool / Manual Collection",
        event: "ads.submitted_for_analysis",
        howItStarts:
          "A marketer submits a batch of competitor ad examples (screenshots, copy, or URLs) for hook extraction",
        setup:
          "POST competitor ad data including ad copy text, platform, advertiser name, and any performance signals to the workflow URL.",
        inputFields: [],
      }),

      // 2. AI extract hooks
      aiNode(
        "2",
        "AI extract advertising hooks",
        280,
        "You are an advertising copywriter and hook specialist. Analyze these competitor ads and extract every distinct hook pattern. A hook is the opening line/concept that grabs attention. Identify the mechanism, promise, curiosity gap, or pattern interrupt used. Respond JSON: {hooks: Array<{hook_text: string, hook_type: string, mechanism: string, source_advertiser: string, platform: string}>, total_hooks: number}",
        "{webhook_payload}",
        [
          { field: "hooks", as: "extracted_hooks", type: "string" },
          { field: "total_hooks", as: "hook_count", type: "number" },
        ],
      ),

      // 3. AI categorize hooks
      aiNode(
        "3",
        "AI categorize hooks by psychological lever",
        520,
        "You are a consumer psychology expert. Categorize each hook by its primary psychological lever: curiosity gap, social proof, authority, scarcity/urgency, fear of missing out, pain agitation, aspiration, contrarian/pattern interrupt, specificity, or story hook. Respond JSON: {categorized_hooks: Array<{hook_text: string, category: string, psychological_lever: string, effectiveness_hypothesis: string}>, category_distribution: object}",
        "Hooks: {extracted_hooks}",
        [
          {
            field: "categorized_hooks",
            as: "categorized_hooks",
            type: "string",
          },
          {
            field: "category_distribution",
            as: "category_distribution",
            type: "string",
          },
        ],
      ),

      // 4. AI adapt hooks for our brand
      aiNode(
        "4",
        "AI adapt hooks for our brand",
        760,
        "You are a creative director. For each competitor hook, create 2-3 adapted versions for our brand. Maintain the psychological mechanism but change the specifics, voice, and offer. Never copy verbatim. Respond JSON: {adapted_hooks: Array<{original: string, category: string, adaptations: string[], brand_fit_score: number}>, total_adaptations: number}",
        "Original hooks: {categorized_hooks}. Our brand voice: professional but approachable. Our product category: SaaS/marketing automation.",
        [
          { field: "adapted_hooks", as: "adapted_hooks", type: "string" },
          {
            field: "total_adaptations",
            as: "adaptation_count",
            type: "number",
          },
        ],
      ),

      // 5. AI score and rank
      aiNode(
        "5",
        "AI score and rank all hooks",
        1000,
        "You are a performance marketing analyst. Score each adapted hook on: attention-grab (1-10), brand alignment (1-10), testability (1-10), versatility across platforms (1-10). Calculate composite score. Respond JSON: {scored_hooks: Array<{hook: string, category: string, attention: number, brand_fit: number, testability: number, versatility: number, composite: number}>, top_10: string[]}",
        "Adapted hooks: {adapted_hooks}",
        [
          { field: "scored_hooks", as: "scored_hooks", type: "string" },
          { field: "top_10", as: "top_10_hooks", type: "string" },
        ],
      ),

      // 6. Publish to Google Sheets
      n("6", "hitApi", "Publish hook library to Google Sheets", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/HookLibrary!A:H:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "values", value: "{scored_hooks}" }],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "hooks_published",
              type: "number",
            },
          ],
        },
      }),

      // 7. Slack notification
      slackNode(
        "7",
        "Notify creative team: hook library updated",
        1480,
        "🎣 *Hook Library Updated*\\nHooks Extracted: {hook_count}\\nAdaptations Created: {adaptation_count}\\n\\n*Category Distribution:*\\n{category_distribution}\\n\\n*Top 10 Hooks:*\\n{top_10_hooks}\\n\\nFull library published to Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 183. HUBSPOT CONTACT ENRICHMENT (7 nodes)
  // ===================================================================

  "HubSpot Contact Enrichment": {
    summary:
      "Triggers on new CRM contacts, enriches via Clearbit, generates AI insights, checks for data conflicts before merging, writes enriched data back to HubSpot, evaluates lead score impact, and alerts on data quality issues.",
    nodes: [
      trigger("Webhook: New HubSpot contact created", "webhook_app", {
        source: "HubSpot CRM",
        event: "contact.creation",
        howItStarts:
          "A new contact is created in HubSpot CRM and needs enrichment",
        setup:
          "In HubSpot, create a workflow that triggers on contact creation and sends a webhook to the workflow URL with the contact's vid, email, and any existing properties.",
        inputFields: [],
      }),

      // 2. Clearbit enrichment
      n("2", "hitApi", "Clearbit enrich contact", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://person-stream.clearbit.com/v2/combined/find?email={contact_email}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          body: [],
          variables: [
            {
              name: "person.name.fullName",
              customName: "enriched_name",
              type: "string",
            },
            {
              name: "person.employment.title",
              customName: "enriched_title",
              type: "string",
            },
            {
              name: "person.employment.seniority",
              customName: "enriched_seniority",
              type: "string",
            },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "enriched_employees",
              type: "number",
            },
            {
              name: "company.category.industry",
              customName: "enriched_industry",
              type: "string",
            },
            {
              name: "company.metrics.annualRevenue",
              customName: "enriched_revenue",
              type: "string",
            },
            {
              name: "company.tech",
              customName: "enriched_tech",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI generate insights
      aiNode(
        "3",
        "AI generate contact insights",
        520,
        "You are a sales intelligence analyst. Based on the enriched data, generate insights: ideal customer profile fit, buying signals, recommended engagement approach, and potential deal size estimate. Respond JSON: {icp_fit_score: number, icp_fit_reason: string, buying_signals: string[], engagement_approach: string, deal_size_estimate: string, lead_tier: string}",
        "Contact: {contact_email}. Name: {enriched_name}. Title: {enriched_title}. Seniority: {enriched_seniority}. Company: {enriched_company}. Employees: {enriched_employees}. Industry: {enriched_industry}. Revenue: {enriched_revenue}. Tech: {enriched_tech}",
        [
          { field: "icp_fit_score", as: "icp_score", type: "number" },
          { field: "icp_fit_reason", as: "icp_reason", type: "string" },
          { field: "buying_signals", as: "buying_signals", type: "string" },
          {
            field: "engagement_approach",
            as: "engagement_approach",
            type: "string",
          },
          { field: "lead_tier", as: "lead_tier", type: "string" },
        ],
      ),

      // 4. Conflict-aware merge condition
      n("4", "condition", "Check for data conflicts before merge", 760, {
        conditionDetails: {
          conditions: [
            { field: "enriched_name", operator: "isNotEmpty", value: "" },
          ],
        },
      }),

      // 5. HubSpot CRM writeback
      n("5", "hitApi", "HubSpot CRM writeback enriched data", 1000, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_vid}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"jobtitle":"{enriched_title}","company":"{enriched_company}","industry":"{enriched_industry}","numberofemployees":"{enriched_employees}","annualrevenue":"{enriched_revenue}","hs_lead_status":"Enriched","icp_score":"{icp_score}","lead_tier":"{lead_tier}"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_update_id", type: "string" },
          ],
        },
      }),

      // 6. Lead score impact check
      n("6", "condition", "Check if lead score warrants alert", 1240, {
        conditionDetails: {
          conditions: [
            { field: "icp_score", operator: "greaterThan", value: "80" },
          ],
        },
      }),

      // 7. Data quality Slack alert
      slackNode(
        "7",
        "Slack alert: enrichment complete",
        1480,
        "🏢 *Contact Enriched*\\nContact: {enriched_name} ({contact_email})\\nTitle: {enriched_title} @ {enriched_company}\\nICP Score: {icp_score}/100 — Tier: {lead_tier}\\n\\n*Buying Signals:*\\n{buying_signals}\\n\\n*Recommended Approach:*\\n{engagement_approach}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 184. IMAGE PROMPT SHEET FOR AI CREATIVE (7 nodes)
  // ===================================================================

  "Image Prompt Sheet for AI Creative": {
    summary:
      "Ingests a campaign brief, extracts visual requirements with AI, generates DALL-E prompts, generates Midjourney prompts, appends technical specs, publishes to Google Sheets, and notifies the creative team.",
    nodes: [
      trigger("Webhook: Campaign brief for AI image prompts", "webhook_app", {
        source: "Creative Brief Tool / Slack",
        event: "creative_brief.submitted",
        howItStarts:
          "A campaign brief is submitted and needs AI-generated image prompts for visual assets",
        setup:
          "POST the campaign brief including brand guidelines, target audience, campaign theme, required image sizes, and mood/style preferences to the workflow URL.",
        inputFields: [],
      }),

      // 2. AI extract visual requirements
      aiNode(
        "2",
        "AI extract visual requirements",
        280,
        "You are a creative director. Analyze this campaign brief and extract all visual requirements: mood, color palette, style (photographic/illustration/3D), subjects needed, brand elements to include, target compositions, and forbidden elements. Respond JSON: {mood: string, color_palette: string[], style: string, subjects: string[], brand_elements: string[], compositions: string[], forbidden: string[], image_count_needed: number, aspect_ratios: string[]}",
        "{webhook_payload}",
        [
          { field: "mood", as: "visual_mood", type: "string" },
          { field: "color_palette", as: "color_palette", type: "string" },
          { field: "style", as: "visual_style", type: "string" },
          { field: "subjects", as: "subjects", type: "string" },
          { field: "brand_elements", as: "brand_elements", type: "string" },
          { field: "compositions", as: "compositions", type: "string" },
          { field: "image_count_needed", as: "image_count", type: "number" },
          { field: "aspect_ratios", as: "aspect_ratios", type: "string" },
        ],
      ),

      // 3. AI generate DALL-E prompts
      aiNode(
        "3",
        "AI generate DALL-E prompts",
        520,
        "You are a DALL-E prompt engineer. For each required image, craft an optimized DALL-E 3 prompt. Use descriptive, specific language. Include style, lighting, composition, and quality keywords. Avoid banned content. Respond JSON: {dalle_prompts: Array<{image_name: string, prompt: string, negative_prompt: string, style_preset: string, quality: string}>}",
        "Mood: {visual_mood}. Style: {visual_style}. Colors: {color_palette}. Subjects: {subjects}. Brand elements: {brand_elements}. Compositions: {compositions}. Count: {image_count}",
        [{ field: "dalle_prompts", as: "dalle_prompts", type: "string" }],
      ),

      // 4. AI generate Midjourney prompts
      aiNode(
        "4",
        "AI generate Midjourney prompts",
        760,
        "You are a Midjourney prompt expert. For each required image, craft an optimized Midjourney v6 prompt. Use Midjourney-specific syntax: aspect ratios (--ar), stylize (--s), chaos (--c), quality (--q). Respond JSON: {midjourney_prompts: Array<{image_name: string, prompt: string, parameters: string, style_reference: string}>}",
        "Mood: {visual_mood}. Style: {visual_style}. Colors: {color_palette}. Subjects: {subjects}. Brand elements: {brand_elements}. Compositions: {compositions}. Aspect ratios: {aspect_ratios}",
        [
          {
            field: "midjourney_prompts",
            as: "midjourney_prompts",
            type: "string",
          },
        ],
      ),

      // 5. Append technical specs
      n("5", "runCode", "Append technical specifications", 1000, {
        codeDetails: {
          language: "javascript",
          code: `// Combine prompts with technical specifications
const dalle = JSON.parse(inputs.dalle_prompts || '[]');
const mj = JSON.parse(inputs.midjourney_prompts || '[]');
const ratios = inputs.aspect_ratios || '16:9,1:1,9:16';

const prompt_sheet = dalle.map(function(d, i) {
  const mjPrompt = mj[i] || {};
  return {
    image_name: d.image_name,
    dalle_prompt: d.prompt,
    dalle_negative: d.negative_prompt || '',
    mj_prompt: mjPrompt.prompt || '',
    mj_params: mjPrompt.parameters || '',
    aspect_ratio: ratios.split(',')[i % ratios.split(',').length] || '16:9',
    min_resolution: '2048x2048',
    file_format: 'PNG',
    color_space: 'sRGB'
  };
});

return {
  prompt_sheet: JSON.stringify(prompt_sheet),
  total_prompts: prompt_sheet.length * 2,
  sheet_values: JSON.stringify(prompt_sheet.map(function(p) {
    return [p.image_name, p.dalle_prompt, p.mj_prompt, p.mj_params, p.aspect_ratio, p.min_resolution];
  }))
};`,
          inputVariables: [
            { name: "dalle_prompts", type: "string" },
            { name: "midjourney_prompts", type: "string" },
            { name: "aspect_ratios", type: "string" },
          ],
          outputVariables: [
            { name: "prompt_sheet", type: "string" },
            { name: "total_prompts", type: "number" },
            { name: "sheet_values", type: "string" },
          ],
        },
      }),

      // 6. Publish to Google Sheets
      n("6", "hitApi", "Publish prompt sheet to Google Sheets", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/AIPrompts!A:F:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "values", value: "{sheet_values}" }],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "prompts_published",
              type: "number",
            },
          ],
        },
      }),

      // 7. Notify creative team
      slackNode(
        "7",
        "Notify creative team: prompts ready",
        1480,
        "🎨 *AI Image Prompt Sheet Ready*\\nImages Needed: {image_count}\\nTotal Prompts: {total_prompts} (DALL-E + Midjourney)\\nStyle: {visual_style} | Mood: {visual_mood}\\n\\nPrompt sheet published to Sheets. Ready for generation.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 185. LP BRIEF FROM COMPETITOR (8 nodes)
  // ===================================================================

  "LP Brief from Competitor": {
    summary:
      "Takes a competitor landing page URL, extracts page content, analyzes competitor strategy with AI, identifies differentiation opportunities, generates a full LP brief, suggests wireframe layouts, publishes to Google Docs, and notifies the campaign owner.",
    nodes: [
      trigger(
        "Webhook: Competitor LP URL for brief generation",
        "webhook_app",
        {
          source: "Marketing Team / Competitive Intel",
          event: "lp_brief.requested",
          howItStarts:
            "A marketer submits a competitor landing page URL to generate a differentiated LP brief",
          setup:
            "POST the competitor LP URL, your product/service details, and target audience information to the workflow URL.",
          inputFields: [],
        },
      ),

      // 2. Extract LP content
      n("2", "runCode", "Extract competitor LP content", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Parse the request and extract LP content
const payload = JSON.parse(inputs.webhook_payload || '{}');
const lp_url = payload.competitor_url || payload.url || '';
const our_product = payload.our_product || payload.product || '';
const target_audience = payload.target_audience || payload.audience || '';

// In production, fetch and parse the competitor LP
return {
  lp_url: lp_url,
  lp_headline: '[extracted competitor headline]',
  lp_subheadline: '[extracted competitor subheadline]',
  lp_hero_cta: '[extracted CTA]',
  lp_sections: '[extracted section structure]',
  lp_social_proof: '[extracted testimonials/logos]',
  lp_offer: '[extracted offer details]',
  lp_objection_handling: '[extracted FAQ/objection sections]',
  our_product: our_product,
  target_audience: target_audience
};`,
          inputVariables: [{ name: "webhook_payload", type: "string" }],
          outputVariables: [
            { name: "lp_url", type: "string" },
            { name: "lp_headline", type: "string" },
            { name: "lp_subheadline", type: "string" },
            { name: "lp_hero_cta", type: "string" },
            { name: "lp_sections", type: "string" },
            { name: "lp_social_proof", type: "string" },
            { name: "lp_offer", type: "string" },
            { name: "lp_objection_handling", type: "string" },
            { name: "our_product", type: "string" },
            { name: "target_audience", type: "string" },
          ],
        },
      }),

      // 3. AI competitor strategy analysis
      aiNode(
        "3",
        "AI analyze competitor LP strategy",
        520,
        "You are a CRO and landing page strategist. Analyze this competitor LP's strategy: value proposition positioning, persuasion techniques, page flow, objection handling, social proof strategy, and conversion psychology. Respond JSON: {positioning_strategy: string, persuasion_techniques: string[], page_flow_analysis: string, objection_strategy: string, social_proof_type: string, conversion_psychology: string, strengths: string[], vulnerabilities: string[]}",
        "URL: {lp_url}. Headline: {lp_headline}. Sub: {lp_subheadline}. CTA: {lp_hero_cta}. Sections: {lp_sections}. Social proof: {lp_social_proof}. Offer: {lp_offer}. Objections: {lp_objection_handling}",
        [
          {
            field: "positioning_strategy",
            as: "competitor_positioning",
            type: "string",
          },
          {
            field: "persuasion_techniques",
            as: "persuasion_techniques",
            type: "string",
          },
          { field: "strengths", as: "competitor_strengths", type: "string" },
          {
            field: "vulnerabilities",
            as: "competitor_vulnerabilities",
            type: "string",
          },
        ],
      ),

      // 4. AI differentiation analysis
      aiNode(
        "4",
        "AI identify differentiation opportunities",
        760,
        "You are a positioning expert. Given the competitor's strategy and vulnerabilities, identify specific differentiation opportunities for our product. Focus on positioning gaps, messaging angles the competitor misses, and unique value we can claim. Respond JSON: {differentiation_angles: string[], messaging_gaps: string[], unique_claims: string[], positioning_recommendation: string, competitive_advantages: string[]}",
        "Competitor positioning: {competitor_positioning}. Techniques: {persuasion_techniques}. Strengths: {competitor_strengths}. Vulnerabilities: {competitor_vulnerabilities}. Our product: {our_product}. Audience: {target_audience}",
        [
          {
            field: "differentiation_angles",
            as: "diff_angles",
            type: "string",
          },
          { field: "messaging_gaps", as: "messaging_gaps", type: "string" },
          {
            field: "positioning_recommendation",
            as: "positioning_rec",
            type: "string",
          },
          {
            field: "competitive_advantages",
            as: "competitive_advantages",
            type: "string",
          },
        ],
      ),

      // 5. AI generate LP brief
      aiNode(
        "5",
        "AI generate full LP brief",
        1000,
        "You are a senior conversion copywriter. Generate a complete landing page brief that differentiates from the competitor. Include: headline options, subheadline, hero section copy, section-by-section outline with copy direction, CTA variations, social proof strategy, objection handling plan, and above-the-fold requirements. Respond JSON: {headline_options: string[], subheadline: string, hero_copy: string, sections: Array<{name: string, copy_direction: string, content_notes: string}>, cta_variations: string[], social_proof_plan: string, objection_plan: string, above_fold_requirements: string}",
        "Differentiation: {diff_angles}. Positioning: {positioning_rec}. Advantages: {competitive_advantages}. Messaging gaps: {messaging_gaps}. Product: {our_product}. Audience: {target_audience}",
        [
          { field: "headline_options", as: "headline_options", type: "string" },
          { field: "sections", as: "lp_sections_brief", type: "string" },
          { field: "cta_variations", as: "cta_options", type: "string" },
          {
            field: "social_proof_plan",
            as: "social_proof_plan",
            type: "string",
          },
        ],
      ),

      // 6. AI wireframe suggestions
      aiNode(
        "6",
        "AI suggest wireframe layouts",
        1240,
        "You are a UX designer specializing in high-converting landing pages. Based on the LP brief, suggest 2-3 wireframe layout options. Describe section ordering, visual hierarchy, image placement, and responsive considerations. Respond JSON: {wireframe_options: Array<{name: string, layout_description: string, section_order: string[], visual_hierarchy_notes: string, responsive_notes: string}>, recommended_option: string, design_references: string[]}",
        "Headline options: {headline_options}. Sections: {lp_sections_brief}. CTAs: {cta_options}. Social proof: {social_proof_plan}",
        [
          {
            field: "wireframe_options",
            as: "wireframe_options",
            type: "string",
          },
          {
            field: "recommended_option",
            as: "recommended_wireframe",
            type: "string",
          },
        ],
      ),

      // 7. Publish to Google Docs
      n("7", "hitApi", "Publish LP brief to Google Docs", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://docs.googleapis.com/v1/documents",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "LP Brief vs {lp_url}" },
            {
              key: "body",
              value:
                '{"content":[{"paragraph":{"elements":[{"textRun":{"content":"LP Brief\\nHeadlines: {headline_options}\\nSections: {lp_sections_brief}\\nCTAs: {cta_options}\\nWireframes: {wireframe_options}"}}]}}]}',
            },
          ],
          variables: [
            { name: "documentId", customName: "doc_id", type: "string" },
            { name: "title", customName: "doc_title", type: "string" },
          ],
        },
      }),

      // 8. Slack notification to campaign owner
      slackNode(
        "8",
        "Notify campaign owner: LP brief ready",
        1720,
        "📄 *LP Brief Generated*\\nCompetitor: {lp_url}\\nPositioning: {positioning_rec}\\n\\n*Headline Options:*\\n{headline_options}\\n\\n*CTA Options:*\\n{cta_options}\\n\\n*Recommended Wireframe:* {recommended_wireframe}\\n\\nFull brief in Google Docs: {doc_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 186. OM51 FULL BLOG PIPELINE (10 nodes)
  // ===================================================================

  "OM51 Full Blog Pipeline": {
    summary:
      "Complete blog production pipeline: intake request, SerpAPI keyword research, competitor article analysis, AI outline, AI full draft, automated QA scoring, revision routing, editor approval gate, WordPress publish, and metrics logging.",
    nodes: [
      trigger("Webhook: Blog article request", "webhook_app", {
        source: "Content Calendar / Asana / Slack",
        event: "blog.requested",
        howItStarts:
          "A content request is submitted with a topic, target keyword, and audience details",
        setup:
          "POST the blog request including topic, target keyword, audience persona, word count target, and any specific angles or sources to the workflow URL.",
        inputFields: [],
      }),

      // 2. SerpAPI keyword research
      n("2", "hitApi", "SerpAPI keyword research", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={target_keyword}&api_key={serpapi_key}&num=10",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "serp_results",
              type: "string",
            },
            {
              name: "related_searches",
              customName: "related_keywords",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "search_volume_proxy",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI competitor article analysis
      aiNode(
        "3",
        "AI analyze top-ranking competitor articles",
        520,
        "You are an SEO content analyst. Analyze the top SERP results for this keyword. Identify content patterns, gaps, common structures, word count ranges, and topics covered. Find what is missing that we can uniquely provide. Respond JSON: {competitor_analysis: string, content_gaps: string[], common_structure: string, avg_word_count: number, unique_angles: string[], keyword_opportunities: string[]}",
        "Target keyword: {target_keyword}. SERP results: {serp_results}. Related keywords: {related_keywords}",
        [
          {
            field: "competitor_analysis",
            as: "competitor_analysis",
            type: "string",
          },
          { field: "content_gaps", as: "content_gaps", type: "string" },
          { field: "unique_angles", as: "unique_angles", type: "string" },
          {
            field: "keyword_opportunities",
            as: "keyword_opportunities",
            type: "string",
          },
          { field: "avg_word_count", as: "target_word_count", type: "number" },
        ],
      ),

      // 4. AI detailed outline
      aiNode(
        "4",
        "AI generate detailed article outline",
        760,
        "You are a senior content strategist. Create a comprehensive, SEO-optimized outline that fills the identified content gaps and leverages unique angles. Include H2/H3 structure, key points per section, internal link opportunities, and multimedia suggestions. Respond JSON: {title: string, meta_description: string, outline: Array<{heading: string, level: string, key_points: string[], target_keywords: string[]}>, estimated_word_count: number, internal_link_suggestions: string[], multimedia_notes: string[]}",
        "Keyword: {target_keyword}. Gaps: {content_gaps}. Angles: {unique_angles}. Keywords: {keyword_opportunities}. Competitor analysis: {competitor_analysis}",
        [
          { field: "title", as: "article_title", type: "string" },
          { field: "meta_description", as: "meta_description", type: "string" },
          { field: "outline", as: "article_outline", type: "string" },
          {
            field: "estimated_word_count",
            as: "estimated_words",
            type: "number",
          },
        ],
      ),

      // 5. AI full draft
      aiNode(
        "5",
        "AI generate full article draft",
        1000,
        "You are an expert content writer. Write the complete article following the outline exactly. Write in a professional yet approachable tone. Include data points, examples, and actionable advice. Naturally incorporate target keywords without stuffing. Respond JSON: {article_body: string, word_count: number, keywords_used: string[], readability_level: string}",
        "Title: {article_title}. Outline: {article_outline}. Target keyword: {target_keyword}. Additional keywords: {keyword_opportunities}. Target word count: {estimated_words}",
        [
          { field: "article_body", as: "article_draft", type: "string" },
          { field: "word_count", as: "draft_word_count", type: "number" },
          {
            field: "readability_level",
            as: "readability_level",
            type: "string",
          },
        ],
      ),

      // 6. Automated QA scoring
      n("6", "runCode", "Automated QA scoring pass", 1240, {
        codeDetails: {
          language: "javascript",
          code: `// Score the article on multiple quality dimensions
const draft = inputs.article_draft || '';
const keyword = inputs.target_keyword || '';
const words = draft.split(/\\s+/).filter(Boolean).length;

// Keyword density
const keywordCount = (draft.toLowerCase().match(new RegExp(keyword.toLowerCase(), 'g')) || []).length;
const density = words > 0 ? (keywordCount / words * 100).toFixed(2) : 0;

// Structure checks
const h2Count = (draft.match(/##\\s/g) || []).length;
const h3Count = (draft.match(/###\\s/g) || []).length;
const hasIntro = draft.length > 200;
const hasConclusion = draft.toLowerCase().includes('conclusion') || draft.toLowerCase().includes('key takeaway') || draft.toLowerCase().includes('wrapping up');

// Quality scores
const structureScore = Math.min(10, h2Count * 2 + h3Count);
const lengthScore = words >= inputs.estimated_words * 0.9 ? 10 : Math.round(words / inputs.estimated_words * 10);
const seoScore = density > 0.5 && density < 3 ? 10 : density > 3 ? 5 : 3;
const totalScore = Math.round((structureScore + lengthScore + seoScore) / 3);
const passes_qa = totalScore >= 7 ? 'true' : 'false';

return {
  qa_total_score: totalScore,
  qa_structure_score: structureScore,
  qa_length_score: lengthScore,
  qa_seo_score: seoScore,
  keyword_density: parseFloat(density),
  actual_word_count: words,
  passes_qa: passes_qa,
  qa_notes: 'H2s: ' + h2Count + ', H3s: ' + h3Count + ', Density: ' + density + '%, Words: ' + words
};`,
          inputVariables: [
            { name: "article_draft", type: "string" },
            { name: "target_keyword", type: "string" },
            { name: "estimated_words", type: "number" },
          ],
          outputVariables: [
            { name: "qa_total_score", type: "number" },
            { name: "qa_structure_score", type: "number" },
            { name: "qa_length_score", type: "number" },
            { name: "qa_seo_score", type: "number" },
            { name: "keyword_density", type: "number" },
            { name: "actual_word_count", type: "number" },
            { name: "passes_qa", type: "string" },
            { name: "qa_notes", type: "string" },
          ],
        },
      }),

      // 7. Revision condition
      n("7", "condition", "Check if draft passes QA threshold", 1480, {
        conditionDetails: {
          conditions: [
            { field: "passes_qa", operator: "equals", value: "true" },
          ],
        },
      }),

      // 8. Editor approval gate (True branch)
      n("8", "approvalGate", "Editor approval gate", 1720, {
        approvalGateDetails: {
          approvers: ["{editor_email}"],
          message:
            "Blog draft ready for review: '{article_title}'. QA Score: {qa_total_score}/10. Word count: {actual_word_count}. Keyword density: {keyword_density}%. Notes: {qa_notes}",
          timeoutHours: 48,
        },
      }),

      // 9. WordPress CMS publish
      n("9", "hitApi", "Publish to WordPress CMS", 1960, {
        hitApiDetails: {
          method: "POST",
          url: "https://{wordpress_domain}/wp-json/wp/v2/posts",
          headers: [
            { key: "Authorization", value: "Basic {wp_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{article_title}" },
            { key: "content", value: "{article_draft}" },
            { key: "status", value: "publish" },
            {
              key: "meta",
              value:
                '{"_yoast_wpseo_metadesc":"{meta_description}","_yoast_wpseo_focuskw":"{target_keyword}"}',
            },
          ],
          variables: [
            { name: "id", customName: "wp_post_id", type: "number" },
            { name: "link", customName: "wp_post_url", type: "string" },
          ],
        },
      }),

      // 10. Log publishing metrics
      n("10", "hitApi", "Log publishing metrics to Sheets", 2200, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/BlogPipeline!A:I:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                "[['{article_title}','{target_keyword}','{actual_word_count}','{keyword_density}','{qa_total_score}','{readability_level}','{wp_post_url}','{wp_post_id}']]",
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "metrics_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "5", "output_2"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 187. OM52 BATCH ARTICLE QUEUE (8 nodes)
  // ===================================================================

  "OM52 Batch Article Queue": {
    summary:
      "Pulls a batch of article briefs from Airtable, validates the queue, drafts each article with AI, scores quality per draft, applies a quality threshold gate, schedules to CMS, writes status back to Airtable, and sends a batch summary report.",
    nodes: [
      trigger("Webhook: Batch article queue trigger", "webhook_app", {
        source: "Airtable / Content Calendar",
        event: "batch.ready",
        howItStarts:
          "A batch of article briefs is marked as ready in Airtable and queued for automated drafting",
        setup:
          "In Airtable, set up an automation that fires when a batch view has 3+ records with status 'Ready for Draft'. POST the batch ID to the workflow URL.",
        inputFields: [],
      }),

      // 2. Validate queue from Airtable
      n("2", "runCode", "Validate batch queue from Airtable", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Validate the batch and extract brief details
const payload = JSON.parse(inputs.webhook_payload || '{}');
const batch_id = payload.batch_id || payload.id || 'batch-' + Date.now();
const records = payload.records || [];

const validated = records.filter(function(r) {
  return r.fields && r.fields.topic && r.fields.keyword;
}).map(function(r) {
  return {
    record_id: r.id,
    topic: r.fields.topic,
    keyword: r.fields.keyword,
    word_count: r.fields.word_count || 1500,
    audience: r.fields.audience || 'general',
    status: 'queued'
  };
});

return {
  batch_id: batch_id,
  validated_briefs: JSON.stringify(validated),
  total_in_batch: validated.length,
  invalid_count: records.length - validated.length,
  queue_valid: validated.length > 0 ? 'true' : 'false'
};`,
          inputVariables: [{ name: "webhook_payload", type: "string" }],
          outputVariables: [
            { name: "batch_id", type: "string" },
            { name: "validated_briefs", type: "string" },
            { name: "total_in_batch", type: "number" },
            { name: "invalid_count", type: "number" },
            { name: "queue_valid", type: "string" },
          ],
        },
      }),

      // 3. AI draft articles from briefs
      aiNode(
        "3",
        "AI draft articles from batch briefs",
        520,
        "You are a prolific content writer. Given a batch of article briefs, write a complete draft for each. Follow each brief's topic, keyword, word count, and audience. Respond JSON: {drafts: Array<{record_id: string, title: string, body: string, word_count: number, keyword: string}>, drafts_completed: number}",
        "Briefs: {validated_briefs}",
        [
          { field: "drafts", as: "batch_drafts", type: "string" },
          { field: "drafts_completed", as: "drafts_completed", type: "number" },
        ],
      ),

      // 4. AI QA score each draft
      aiNode(
        "4",
        "AI QA score each draft",
        760,
        "You are a content quality auditor. Score each draft on: keyword usage (1-10), structure quality (1-10), readability (1-10), uniqueness (1-10), and actionability (1-10). Calculate overall score. Flag any that need revision. Respond JSON: {scored_drafts: Array<{record_id: string, title: string, overall_score: number, keyword_score: number, structure_score: number, passes_threshold: boolean, revision_notes: string}>, pass_count: number, fail_count: number}",
        "Drafts: {batch_drafts}",
        [
          { field: "scored_drafts", as: "scored_drafts", type: "string" },
          { field: "pass_count", as: "pass_count", type: "number" },
          { field: "fail_count", as: "fail_count", type: "number" },
        ],
      ),

      // 5. Quality threshold condition
      n("5", "condition", "Check if batch passes quality threshold", 1000, {
        conditionDetails: {
          conditions: [{ field: "fail_count", operator: "equals", value: "0" }],
        },
      }),

      // 6. CMS scheduling
      n("6", "hitApi", "Schedule approved articles to CMS", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{wordpress_domain}/wp-json/wp/v2/posts",
          headers: [
            { key: "Authorization", value: "Basic {wp_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "Batch {batch_id} articles" },
            { key: "status", value: "draft" },
            { key: "content", value: "{scored_drafts}" },
          ],
          variables: [
            { name: "id", customName: "cms_batch_id", type: "number" },
          ],
        },
      }),

      // 7. Write status back to Airtable
      n("7", "hitApi", "Airtable status writeback", 1480, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.airtable.com/v0/{airtable_base_id}/{airtable_table_name}",
          headers: [
            { key: "Authorization", value: "Bearer {airtable_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "records", value: "{scored_drafts}" }],
          variables: [
            { name: "records", customName: "updated_records", type: "string" },
          ],
        },
      }),

      // 8. Batch summary Slack report
      slackNode(
        "8",
        "Slack batch summary report",
        1720,
        "📦 *Batch Article Queue Complete*\\nBatch: {batch_id}\\nTotal Briefs: {total_in_batch}\\nDrafts Completed: {drafts_completed}\\nPassed QA: {pass_count}\\nFailed QA: {fail_count}\\nInvalid Briefs: {invalid_count}\\n\\nAll passing articles scheduled in CMS. Airtable updated.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 188. OM53 BRAND-VOICE DRAFTING (7 nodes)
  // ===================================================================

  "OM53 Brand-Voice Drafting": {
    summary:
      "Takes a draft request, retrieves brand voice guidelines, calibrates the AI system prompt for voice consistency, generates the draft, audits voice consistency, routes for revision or human review if needed, and delivers the final version.",
    nodes: [
      trigger("Webhook: Brand-voice draft request", "webhook_app", {
        source: "Content Team / Slack",
        event: "draft.requested",
        howItStarts:
          "A content request is submitted that requires strict brand voice adherence",
        setup:
          "POST the draft request including content type, topic, target audience, length, and any specific brand voice profile ID to the workflow URL.",
        inputFields: [],
      }),

      // 2. Retrieve brand voice profile
      n("2", "hitApi", "Retrieve brand voice guidelines", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{brand_guide_sheet}/values/BrandVoice!A:F",
          headers: [{ key: "Authorization", value: "Bearer {google_token}" }],
          body: [],
          variables: [
            { name: "values", customName: "brand_voice_data", type: "string" },
          ],
        },
      }),

      // 3. Calibrate system prompt for voice
      n("3", "runCode", "Calibrate AI system prompt for brand voice", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Build a calibrated system prompt from brand voice guidelines
const voiceData = JSON.parse(inputs.brand_voice_data || '[]');
const payload = JSON.parse(inputs.webhook_payload || '{}');

// Extract brand voice attributes
const tone = voiceData[0] ? voiceData[0][0] : 'professional and approachable';
const vocabulary = voiceData[1] ? voiceData[1][0] : 'clear, jargon-free';
const sentence_style = voiceData[2] ? voiceData[2][0] : 'mix of short and medium';
const forbidden_words = voiceData[3] ? voiceData[3][0] : 'synergy, leverage, utilize';
const examples = voiceData[4] ? voiceData[4][0] : '';

const system_prompt = 'You are a content writer who MUST write in this exact brand voice: '
  + 'Tone: ' + tone + '. '
  + 'Vocabulary: ' + vocabulary + '. '
  + 'Sentence style: ' + sentence_style + '. '
  + 'NEVER use these words: ' + forbidden_words + '. '
  + 'Voice examples: ' + examples;

return {
  calibrated_prompt: system_prompt,
  content_type: payload.content_type || 'article',
  topic: payload.topic || '',
  target_audience: payload.audience || '',
  word_count_target: payload.word_count || 800,
  requester_email: payload.requester || ''
};`,
          inputVariables: [
            { name: "brand_voice_data", type: "string" },
            { name: "webhook_payload", type: "string" },
          ],
          outputVariables: [
            { name: "calibrated_prompt", type: "string" },
            { name: "content_type", type: "string" },
            { name: "topic", type: "string" },
            { name: "target_audience", type: "string" },
            { name: "word_count_target", type: "number" },
            { name: "requester_email", type: "string" },
          ],
        },
      }),

      // 4. AI generate draft with calibrated voice
      aiNode(
        "4",
        "AI generate brand-voice draft",
        760,
        "{calibrated_prompt}",
        "Write a {content_type} about: {topic}. Target audience: {target_audience}. Target word count: {word_count_target}. Make it engaging, well-structured, and true to the brand voice described in your instructions.",
        [
          { field: "draft", as: "brand_draft", type: "string" },
          { field: "word_count", as: "draft_word_count", type: "number" },
        ],
      ),

      // 5. Voice consistency audit condition
      n("5", "condition", "Voice consistency audit check", 1000, {
        conditionDetails: {
          conditions: [
            {
              field: "draft_word_count",
              operator: "greaterThan",
              value: "100",
            },
          ],
        },
      }),

      // 6a. Revision or human review (True = passes, deliver; False = flag for review)
      slackNode(
        "6",
        "Deliver draft via Slack and Docs",
        1240,
        "✍️ *Brand-Voice Draft Ready*\\nType: {content_type}\\nTopic: {topic}\\nWord Count: {draft_word_count}\\n\\n{brand_draft}\\n\\nVoice calibration applied. Ready for final review.",
      ),

      // 6b. Human review flag
      emailNode(
        "7",
        "Flag for human voice review",
        1240,
        "{requester_email}",
        "Brand Voice Draft Needs Review: {topic}",
        "The automated brand-voice draft for '{topic}' requires manual voice review. The draft was generated but may need voice calibration adjustments.\\n\\nDraft: {brand_draft}",
        500,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 189. OM54 FACT-CHECK PASS (7 nodes)
  // ===================================================================

  "OM54 Fact-Check Pass": {
    summary:
      "Ingests a draft article, extracts factual claims with AI, searches for evidence via SerpAPI, verifies each claim, generates a fact-check report, routes by risk level, and delivers to the editorial team.",
    nodes: [
      trigger("Webhook: Article draft for fact-checking", "webhook_app", {
        source: "CMS / Content Pipeline",
        event: "draft.ready_for_factcheck",
        howItStarts:
          "A draft article is submitted for automated fact-checking before publication",
        setup:
          "POST the article draft content, title, and author to the workflow URL. The system will extract claims and verify them.",
        inputFields: [],
      }),

      // 2. AI extract factual claims
      aiNode(
        "2",
        "AI extract factual claims from draft",
        280,
        "You are a fact-checker. Read this article draft and extract every factual claim, statistic, quote, date, and verifiable statement. Number each claim. Respond JSON: {claims: Array<{claim_number: number, claim_text: string, claim_type: string, source_cited: string, verification_priority: string}>, total_claims: number, high_priority_claims: number}",
        "{webhook_payload}",
        [
          { field: "claims", as: "extracted_claims", type: "string" },
          { field: "total_claims", as: "total_claims", type: "number" },
          {
            field: "high_priority_claims",
            as: "high_priority_claims",
            type: "number",
          },
        ],
      ),

      // 3. SerpAPI evidence search
      n("3", "hitApi", "SerpAPI search for claim evidence", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={extracted_claims}&api_key={serpapi_key}&num=5",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "evidence_results",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI verify claims against evidence
      aiNode(
        "4",
        "AI verify claims against evidence",
        760,
        "You are a senior fact-checker. For each claim, compare it against the search evidence. Mark each as: verified, partially_verified, unverified, or contradicted. Provide the source for verification. Respond JSON: {verified_claims: Array<{claim_number: number, claim_text: string, verdict: string, confidence: number, source: string, notes: string}>, verified_count: number, contradicted_count: number, unverified_count: number, overall_risk: string}",
        "Claims: {extracted_claims}. Evidence: {evidence_results}",
        [
          {
            field: "verified_claims",
            as: "verification_results",
            type: "string",
          },
          { field: "verified_count", as: "verified_count", type: "number" },
          {
            field: "contradicted_count",
            as: "contradicted_count",
            type: "number",
          },
          { field: "unverified_count", as: "unverified_count", type: "number" },
          { field: "overall_risk", as: "fact_check_risk", type: "string" },
        ],
      ),

      // 5. AI generate fact-check report
      aiNode(
        "5",
        "AI generate fact-check report",
        1000,
        "You are an editorial quality analyst. Generate a comprehensive fact-check report with executive summary, claim-by-claim results, risk assessment, and recommended changes. Respond JSON: {executive_summary: string, report_body: string, recommended_changes: string[], risk_assessment: string, publish_recommendation: string}",
        "Verification results: {verification_results}. Total claims: {total_claims}. Verified: {verified_count}. Contradicted: {contradicted_count}. Unverified: {unverified_count}. Risk: {fact_check_risk}",
        [
          { field: "executive_summary", as: "fc_summary", type: "string" },
          { field: "recommended_changes", as: "fc_changes", type: "string" },
          {
            field: "risk_assessment",
            as: "fc_risk_assessment",
            type: "string",
          },
          {
            field: "publish_recommendation",
            as: "publish_recommendation",
            type: "string",
          },
        ],
      ),

      // 6. Risk routing condition
      n("6", "condition", "Route by fact-check risk level", 1240, {
        conditionDetails: {
          conditions: [
            { field: "contradicted_count", operator: "equals", value: "0" },
          ],
        },
      }),

      // 7. Editorial Slack delivery
      slackNode(
        "7",
        "Deliver fact-check report to editorial",
        1480,
        "🔍 *Fact-Check Report*\\nRisk: {fact_check_risk}\\nClaims: {total_claims} total | {verified_count} verified | {contradicted_count} contradicted | {unverified_count} unverified\\n\\n*Summary:*\\n{fc_summary}\\n\\n*Publish Recommendation:* {publish_recommendation}\\n\\n*Required Changes:*\\n{fc_changes}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 190. OM55 STATISTIC VERIFICATION (7 nodes)
  // ===================================================================

  "OM55 Statistic Verification": {
    summary:
      "Extracts statistics from content, searches for primary sources via SerpAPI, compares accuracy, finds updated data, generates a verification report, updates the stat library in Sheets, and delivers to editorial.",
    nodes: [
      trigger(
        "Webhook: Content with statistics for verification",
        "webhook_app",
        {
          source: "Content Pipeline / Editorial",
          event: "stats.verification_requested",
          howItStarts:
            "An article or content piece containing statistics is submitted for verification of all cited numbers",
          setup:
            "POST the article content to the workflow URL. The system will extract all statistics and verify each against primary sources.",
          inputFields: [],
        },
      ),

      // 2. SerpAPI primary source search
      aiNode(
        "2",
        "AI extract statistics and search context",
        280,
        "You are a research analyst. Extract every statistic, number, percentage, and data point from this content. For each, note the claimed source and context. Respond JSON: {statistics: Array<{stat_text: string, claimed_value: string, claimed_source: string, context: string, search_query: string}>, total_stats: number}",
        "{webhook_payload}",
        [
          { field: "statistics", as: "extracted_stats", type: "string" },
          { field: "total_stats", as: "total_stats", type: "number" },
        ],
      ),

      // 3. SerpAPI primary source search
      n("3", "hitApi", "SerpAPI search for primary stat sources", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={extracted_stats}+statistic+source&api_key={serpapi_key}&num=5",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "source_results",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI accuracy comparison
      aiNode(
        "4",
        "AI compare stat accuracy against sources",
        760,
        "You are a data verification specialist. For each statistic, compare the claimed value against the primary sources found. Check if the stat is current, accurately cited, and from a credible source. Respond JSON: {verifications: Array<{stat_text: string, claimed_value: string, actual_value: string, is_accurate: boolean, is_current: boolean, primary_source: string, source_date: string, needs_update: boolean}>, accurate_count: number, outdated_count: number, inaccurate_count: number}",
        "Statistics: {extracted_stats}. Source search results: {source_results}",
        [
          { field: "verifications", as: "stat_verifications", type: "string" },
          { field: "accurate_count", as: "accurate_count", type: "number" },
          { field: "outdated_count", as: "outdated_count", type: "number" },
          { field: "inaccurate_count", as: "inaccurate_count", type: "number" },
        ],
      ),

      // 5. AI generate updated data and verification report
      aiNode(
        "5",
        "AI generate verification report with updates",
        1000,
        "You are an editorial data analyst. Generate a complete verification report. For any outdated or inaccurate stats, provide the correct current value with source citation. Respond JSON: {report_summary: string, corrections: Array<{original: string, corrected: string, source: string}>, verification_confidence: string, editorial_notes: string}",
        "Verifications: {stat_verifications}. Accurate: {accurate_count}. Outdated: {outdated_count}. Inaccurate: {inaccurate_count}",
        [
          {
            field: "report_summary",
            as: "verification_summary",
            type: "string",
          },
          { field: "corrections", as: "stat_corrections", type: "string" },
          {
            field: "verification_confidence",
            as: "verification_confidence",
            type: "string",
          },
        ],
      ),

      // 6. Update stat library in Sheets
      n("6", "hitApi", "Update stat library in Google Sheets", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/StatLibrary!A:F:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "values", value: "{stat_verifications}" }],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "stats_logged",
              type: "number",
            },
          ],
        },
      }),

      // 7. Editorial delivery
      slackNode(
        "7",
        "Deliver verification report to editorial",
        1480,
        "📊 *Statistic Verification Report*\\nTotal Stats: {total_stats}\\nAccurate: {accurate_count} | Outdated: {outdated_count} | Inaccurate: {inaccurate_count}\\nConfidence: {verification_confidence}\\n\\n*Summary:*\\n{verification_summary}\\n\\n*Corrections Needed:*\\n{stat_corrections}\\n\\nStat library updated in Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 191. OM56 EXPERT QUOTE INSERTION (8 nodes)
  // ===================================================================

  "OM56 Expert Quote Insertion": {
    summary:
      "Takes an article, detects quote opportunities with AI, searches for expert quotes via SerpAPI, verifies credentials, selects the best quotes, inserts them contextually, runs an EEAT/credibility pass, and notifies the editor with highlights.",
    nodes: [
      trigger("Webhook: Article for expert quote insertion", "webhook_app", {
        source: "Content Pipeline / Editorial",
        event: "article.needs_quotes",
        howItStarts:
          "An article draft is submitted that needs expert quotes inserted for EEAT credibility",
        setup:
          "POST the article draft content to the workflow URL. The system will identify quote insertion points and find relevant expert quotes.",
        inputFields: [],
      }),

      // 2. AI detect quote opportunities
      aiNode(
        "2",
        "AI detect quote insertion opportunities",
        280,
        "You are an editorial quality specialist. Read this article and identify the best places to insert expert quotes. Look for claims that need authority backing, sections that would benefit from perspective, and areas where a quote would improve credibility. Respond JSON: {opportunities: Array<{section: string, context: string, quote_type_needed: string, search_query: string, insertion_point: string}>, total_opportunities: number, article_topic: string}",
        "{webhook_payload}",
        [
          { field: "opportunities", as: "quote_opportunities", type: "string" },
          {
            field: "total_opportunities",
            as: "opportunity_count",
            type: "number",
          },
          { field: "article_topic", as: "article_topic", type: "string" },
        ],
      ),

      // 3. SerpAPI expert quote search
      n("3", "hitApi", "SerpAPI search for expert quotes", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={article_topic}+expert+quote+opinion&api_key={serpapi_key}&num=10",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "quote_search_results",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI verify credentials
      aiNode(
        "4",
        "AI verify expert credentials",
        760,
        "You are a research verification specialist. For each potential expert quote found, verify the person's credentials: check their title, organization, relevant expertise, publication history, and authority in this topic area. Respond JSON: {verified_experts: Array<{name: string, title: string, organization: string, expertise_area: string, credential_score: number, quote: string, source_url: string}>, total_verified: number}",
        "Topic: {article_topic}. Quote opportunities: {quote_opportunities}. Search results: {quote_search_results}",
        [
          { field: "verified_experts", as: "verified_experts", type: "string" },
          { field: "total_verified", as: "verified_count", type: "number" },
        ],
      ),

      // 5. AI select best quotes and insert contextually
      aiNode(
        "5",
        "AI select and contextually insert quotes",
        1000,
        "You are a senior editor. Select the best expert quote for each insertion opportunity. Write smooth transition sentences that introduce the expert and their quote naturally. Output the article sections with quotes inserted. Respond JSON: {inserted_sections: Array<{section: string, expert_name: string, quote: string, transition_text: string}>, quotes_inserted: number, article_with_quotes: string}",
        "Opportunities: {quote_opportunities}. Verified experts: {verified_experts}",
        [
          {
            field: "inserted_sections",
            as: "inserted_sections",
            type: "string",
          },
          { field: "quotes_inserted", as: "quotes_inserted", type: "number" },
          {
            field: "article_with_quotes",
            as: "article_with_quotes",
            type: "string",
          },
        ],
      ),

      // 6. AI EEAT/credibility pass
      aiNode(
        "6",
        "AI EEAT credibility assessment",
        1240,
        "You are a Google EEAT specialist. Assess the article with inserted quotes for Experience, Expertise, Authoritativeness, and Trustworthiness. Score each dimension. Identify remaining gaps. Respond JSON: {experience_score: number, expertise_score: number, authority_score: number, trust_score: number, overall_eeat: number, remaining_gaps: string[], improvement_suggestions: string[], passes_eeat: boolean}",
        "Article with quotes: {article_with_quotes}. Experts used: {verified_experts}. Topic: {article_topic}",
        [
          { field: "overall_eeat", as: "eeat_score", type: "number" },
          { field: "remaining_gaps", as: "eeat_gaps", type: "string" },
          { field: "passes_eeat", as: "passes_eeat", type: "string" },
          {
            field: "improvement_suggestions",
            as: "eeat_suggestions",
            type: "string",
          },
        ],
      ),

      // 7. EEAT pass condition
      n("7", "condition", "Check if EEAT threshold met", 1480, {
        conditionDetails: {
          conditions: [
            { field: "eeat_score", operator: "greaterThan", value: "6" },
          ],
        },
      }),

      // 8. Editor notification with highlights
      slackNode(
        "8",
        "Notify editor: quotes inserted with highlights",
        1720,
        "💬 *Expert Quotes Inserted*\\nTopic: {article_topic}\\nQuotes Added: {quotes_inserted}/{opportunity_count}\\nEEAT Score: {eeat_score}/10\\n\\n*Inserted Sections:*\\n{inserted_sections}\\n\\n*EEAT Gaps:*\\n{eeat_gaps}\\n\\n*Suggestions:*\\n{eeat_suggestions}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 192. OM57 OUTLINE GENERATOR (7 nodes)
  // ===================================================================

  "OM57 Outline Generator": {
    summary:
      "Takes a target keyword, analyzes the SERP via SerpAPI, extracts competitor structures, identifies gaps with AI, generates a differentiated outline, adds SEO annotations, and delivers to editorial via Google Docs.",
    nodes: [
      trigger("Webhook: Keyword for outline generation", "webhook_app", {
        source: "SEO Tool / Content Calendar",
        event: "outline.requested",
        howItStarts:
          "A target keyword is submitted for automated article outline generation",
        setup:
          "POST the target keyword, secondary keywords, target audience, and content angle to the workflow URL.",
        inputFields: [],
      }),

      // 2. SerpAPI SERP analysis
      n("2", "hitApi", "SerpAPI analyze SERP for keyword", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={target_keyword}&api_key={serpapi_key}&num=10",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "serp_organic",
              type: "string",
            },
            {
              name: "related_searches",
              customName: "related_searches",
              type: "string",
            },
            {
              name: "answer_box",
              customName: "featured_snippet",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI extract competitor structures
      aiNode(
        "3",
        "AI extract competitor content structures",
        520,
        "You are an SEO analyst. Analyze the top-ranking results and extract their content structures: headings, section topics, content types, word counts, and unique elements. Respond JSON: {competitor_structures: Array<{title: string, headings: string[], word_count_estimate: number, content_type: string, unique_elements: string[]}>, common_headings: string[], content_pattern: string}",
        "Keyword: {target_keyword}. SERP results: {serp_organic}. Featured snippet: {featured_snippet}",
        [
          {
            field: "competitor_structures",
            as: "competitor_structures",
            type: "string",
          },
          { field: "common_headings", as: "common_headings", type: "string" },
          { field: "content_pattern", as: "content_pattern", type: "string" },
        ],
      ),

      // 4. AI gap analysis
      aiNode(
        "4",
        "AI identify content gaps and opportunities",
        760,
        "You are a content strategist. Compare competitor structures to find gaps: topics none of them cover well, questions left unanswered, data/examples missing, and unique angles not explored. Respond JSON: {gaps: string[], unanswered_questions: string[], missing_data: string[], unique_angle_opportunities: string[], featured_snippet_opportunity: string}",
        "Competitor structures: {competitor_structures}. Common headings: {common_headings}. Pattern: {content_pattern}. Related searches: {related_searches}. Featured snippet: {featured_snippet}",
        [
          { field: "gaps", as: "content_gaps", type: "string" },
          {
            field: "unanswered_questions",
            as: "unanswered_questions",
            type: "string",
          },
          {
            field: "unique_angle_opportunities",
            as: "unique_angles",
            type: "string",
          },
          {
            field: "featured_snippet_opportunity",
            as: "snippet_opportunity",
            type: "string",
          },
        ],
      ),

      // 5. AI generate differentiated outline
      aiNode(
        "5",
        "AI generate comprehensive outline",
        1000,
        "You are a senior content editor. Create a comprehensive, differentiated article outline that covers all competitor topics PLUS the identified gaps. Structure with H2/H3 hierarchy, include key points per section, and note where to add unique value. Respond JSON: {title: string, meta_description: string, outline: Array<{level: string, heading: string, key_points: string[], unique_value: string, target_keywords: string[]}>, estimated_word_count: number, content_type: string}",
        "Keyword: {target_keyword}. Gaps: {content_gaps}. Questions: {unanswered_questions}. Angles: {unique_angles}. Snippet opportunity: {snippet_opportunity}. Competitor pattern: {content_pattern}",
        [
          { field: "title", as: "outline_title", type: "string" },
          { field: "meta_description", as: "outline_meta", type: "string" },
          { field: "outline", as: "generated_outline", type: "string" },
          {
            field: "estimated_word_count",
            as: "estimated_words",
            type: "number",
          },
        ],
      ),

      // 6. SEO annotation pass
      n("6", "runCode", "Add SEO annotations to outline", 1240, {
        codeDetails: {
          language: "javascript",
          code: `// Add SEO annotations: internal link opportunities, schema markup suggestions, etc.
const outline = JSON.parse(inputs.generated_outline || '[]');
const keyword = inputs.target_keyword || '';

const annotated = outline.map(function(section) {
  var annotations = [];
  // Suggest internal links for each section
  annotations.push('Internal link opportunity: link to related ' + section.heading.toLowerCase() + ' content');
  // Suggest schema markup where applicable
  if (section.heading.toLowerCase().includes('how') || section.heading.toLowerCase().includes('step')) {
    annotations.push('Schema: HowTo markup recommended');
  }
  if (section.heading.toLowerCase().includes('faq') || section.heading.toLowerCase().includes('question')) {
    annotations.push('Schema: FAQPage markup recommended');
  }
  section.seo_annotations = annotations;
  return section;
});

return {
  annotated_outline: JSON.stringify(annotated),
  section_count: annotated.length,
  schema_opportunities: annotated.filter(function(s) { return s.seo_annotations.some(function(a) { return a.includes('Schema'); }); }).length
};`,
          inputVariables: [
            { name: "generated_outline", type: "string" },
            { name: "target_keyword", type: "string" },
          ],
          outputVariables: [
            { name: "annotated_outline", type: "string" },
            { name: "section_count", type: "number" },
            { name: "schema_opportunities", type: "number" },
          ],
        },
      }),

      // 7. Deliver to Google Docs
      n("7", "hitApi", "Publish outline to Google Docs", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://docs.googleapis.com/v1/documents",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "Outline: {outline_title}" },
            {
              key: "body",
              value:
                '{"content":[{"paragraph":{"elements":[{"textRun":{"content":"Title: {outline_title}\\nMeta: {outline_meta}\\nEstimated Words: {estimated_words}\\nSections: {section_count}\\nSchema Opportunities: {schema_opportunities}\\n\\n{annotated_outline}"}}]}}]}',
            },
          ],
          variables: [
            { name: "documentId", customName: "doc_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 193. OM58 ARTICLE REFRESHER (8 nodes)
  // ===================================================================

  "OM58 Article Refresher": {
    summary:
      "Triggers a content refresh, pulls current content, analyzes ranking and traffic from GA, detects outdated elements with AI, researches updates via SerpAPI, generates a refreshed draft, renders a diff view, and routes to editor approval.",
    nodes: [
      trigger("Webhook: Article refresh trigger", "webhook_app", {
        source: "SEO Dashboard / Content Audit",
        event: "article.refresh_needed",
        howItStarts:
          "An article is flagged for refresh based on declining traffic, outdated content, or ranking drops",
        setup:
          "POST the article URL, WordPress post ID, and reason for refresh to the workflow URL.",
        inputFields: [],
      }),

      // 2. Pull current content
      n("2", "hitApi", "Pull current article from WordPress", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{wordpress_domain}/wp-json/wp/v2/posts/{wp_post_id}",
          headers: [{ key: "Authorization", value: "Basic {wp_auth_token}" }],
          body: [],
          variables: [
            {
              name: "title.rendered",
              customName: "current_title",
              type: "string",
            },
            {
              name: "content.rendered",
              customName: "current_content",
              type: "string",
            },
            { name: "date", customName: "publish_date", type: "string" },
            { name: "modified", customName: "last_modified", type: "string" },
          ],
        },
      }),

      // 3. GA ranking/traffic analysis
      n("3", "hitApi", "Google Analytics performance analysis", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://analyticsdata.googleapis.com/v1beta/properties/{ga_property_id}:runReport",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "dateRanges",
              value: '[{"startDate":"90daysAgo","endDate":"today"}]',
            },
            { key: "dimensions", value: '[{"name":"pagePath"}]' },
            {
              key: "metrics",
              value:
                '[{"name":"sessions"},{"name":"engagementRate"},{"name":"averageSessionDuration"}]',
            },
            {
              key: "dimensionFilter",
              value:
                '{"filter":{"fieldName":"pagePath","stringFilter":{"value":"{article_path}"}}}',
            },
          ],
          variables: [
            { name: "rows", customName: "ga_performance", type: "string" },
          ],
        },
      }),

      // 4. AI detect outdated elements
      aiNode(
        "4",
        "AI detect outdated elements",
        760,
        "You are a content auditor. Analyze this article for outdated elements: stale statistics, broken references, outdated tools/products, changed best practices, old screenshots/examples, and missing current information. Respond JSON: {outdated_elements: Array<{element: string, reason: string, priority: string, section: string}>, freshness_score: number, refresh_scope: string, estimated_effort: string}",
        "Title: {current_title}. Published: {publish_date}. Last modified: {last_modified}. Performance: {ga_performance}. Content: {current_content}",
        [
          {
            field: "outdated_elements",
            as: "outdated_elements",
            type: "string",
          },
          { field: "freshness_score", as: "freshness_score", type: "number" },
          { field: "refresh_scope", as: "refresh_scope", type: "string" },
          { field: "estimated_effort", as: "estimated_effort", type: "string" },
        ],
      ),

      // 5. SerpAPI research updates
      n("5", "hitApi", "SerpAPI research current information", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={current_title}+2024+2025&api_key={serpapi_key}&num=10&tbs=qdr:y",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "update_research",
              type: "string",
            },
          ],
        },
      }),

      // 6. AI generate refreshed draft
      aiNode(
        "6",
        "AI generate refreshed article draft",
        1240,
        "You are a content refresh specialist. Rewrite the outdated sections with current information. Preserve the article's existing structure and voice. Update statistics, examples, and references. Respond JSON: {refreshed_content: string, changes_made: Array<{section: string, change_type: string, description: string}>, total_changes: number, new_word_count: number}",
        "Current content: {current_content}. Outdated elements: {outdated_elements}. Research: {update_research}. Refresh scope: {refresh_scope}",
        [
          { field: "refreshed_content", as: "refreshed_draft", type: "string" },
          { field: "changes_made", as: "changes_made", type: "string" },
          { field: "total_changes", as: "total_changes", type: "number" },
        ],
      ),

      // 7. Render diff
      n("7", "runCode", "Render diff between original and refreshed", 1480, {
        codeDetails: {
          language: "javascript",
          code: `// Generate a readable diff summary
const original = inputs.current_content || '';
const refreshed = inputs.refreshed_draft || '';
const changes = JSON.parse(inputs.changes_made || '[]');

const diff_summary = changes.map(function(c, i) {
  return (i + 1) + '. [' + c.change_type + '] ' + c.section + ': ' + c.description;
}).join('\\n');

const original_words = original.split(/\\s+/).filter(Boolean).length;
const refreshed_words = refreshed.split(/\\s+/).filter(Boolean).length;
const word_diff = refreshed_words - original_words;

return {
  diff_summary: diff_summary,
  original_word_count: original_words,
  refreshed_word_count: refreshed_words,
  word_count_change: word_diff,
  changes_count: changes.length
};`,
          inputVariables: [
            { name: "current_content", type: "string" },
            { name: "refreshed_draft", type: "string" },
            { name: "changes_made", type: "string" },
          ],
          outputVariables: [
            { name: "diff_summary", type: "string" },
            { name: "original_word_count", type: "number" },
            { name: "refreshed_word_count", type: "number" },
            { name: "word_count_change", type: "number" },
            { name: "changes_count", type: "number" },
          ],
        },
      }),

      // 8. Editor approval gate
      n("8", "approvalGate", "Editor approval for refreshed article", 1720, {
        approvalGateDetails: {
          approvers: ["{editor_email}"],
          message:
            "Article refresh ready: '{current_title}'. Freshness score: {freshness_score}/10. Changes: {changes_count}. Word count: {original_word_count} -> {refreshed_word_count} ({word_count_change}).\\n\\nDiff:\\n{diff_summary}",
          timeoutHours: 48,
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 194. OM67 WEBINAR TO BLOG RECAP (7 nodes)
  // ===================================================================

  "OM67 Webinar to Blog Recap": {
    summary:
      "Triggers on webinar completion, cleans up the transcript, extracts key takeaways with AI, drafts a blog recap, generates SEO elements, creates social snippets, and publishes to CMS and social queue.",
    nodes: [
      trigger("Webhook: Webinar completed", "webhook_app", {
        source: "Zoom / Webinar Platform",
        event: "webinar.ended",
        howItStarts:
          "A webinar ends and its recording/transcript is available for content repurposing",
        setup:
          "Connect your webinar platform's webhook to fire when a webinar ends. POST the transcript, title, speakers, and recording URL to the workflow URL.",
        inputFields: [],
      }),

      // 2. Transcript cleanup
      n("2", "runCode", "Clean up webinar transcript", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Clean and structure the webinar transcript
const payload = JSON.parse(inputs.webhook_payload || '{}');
const raw_transcript = payload.transcript || payload.text || '';
const title = payload.title || payload.webinar_title || '';
const speakers = payload.speakers || [];
const recording_url = payload.recording_url || '';

// Clean transcript: remove filler words, fix formatting
var cleaned = raw_transcript
  .replace(/\\b(um|uh|like|you know|so basically|right)\\b/gi, '')
  .replace(/\\s{2,}/g, ' ')
  .trim();

// Estimate duration from word count (avg 150 wpm for speaking)
const word_count = cleaned.split(/\\s+/).filter(Boolean).length;
const estimated_duration = Math.round(word_count / 150);

return {
  cleaned_transcript: cleaned,
  webinar_title: title,
  speakers: JSON.stringify(speakers),
  recording_url: recording_url,
  transcript_word_count: word_count,
  estimated_duration_min: estimated_duration
};`,
          inputVariables: [{ name: "webhook_payload", type: "string" }],
          outputVariables: [
            { name: "cleaned_transcript", type: "string" },
            { name: "webinar_title", type: "string" },
            { name: "speakers", type: "string" },
            { name: "recording_url", type: "string" },
            { name: "transcript_word_count", type: "number" },
            { name: "estimated_duration_min", type: "number" },
          ],
        },
      }),

      // 3. AI extract takeaways
      aiNode(
        "3",
        "AI extract key takeaways",
        520,
        "You are a content strategist. Extract the key takeaways, memorable quotes, actionable insights, and discussion highlights from this webinar transcript. Respond JSON: {key_takeaways: string[], memorable_quotes: Array<{quote: string, speaker: string}>, actionable_insights: string[], discussion_highlights: string[], main_themes: string[]}",
        "Webinar: {webinar_title}. Speakers: {speakers}. Transcript: {cleaned_transcript}",
        [
          { field: "key_takeaways", as: "takeaways", type: "string" },
          { field: "memorable_quotes", as: "quotes", type: "string" },
          { field: "actionable_insights", as: "insights", type: "string" },
          { field: "main_themes", as: "themes", type: "string" },
        ],
      ),

      // 4. AI draft blog recap
      aiNode(
        "4",
        "AI draft blog recap article",
        760,
        "You are a blog writer. Write an engaging blog recap of this webinar. Include an intro that hooks readers who missed the event, structured takeaways, expert quotes, and a CTA to watch the recording. Respond JSON: {blog_title: string, blog_body: string, blog_word_count: number}",
        "Webinar: {webinar_title}. Speakers: {speakers}. Takeaways: {takeaways}. Quotes: {quotes}. Insights: {insights}. Themes: {themes}. Recording: {recording_url}",
        [
          { field: "blog_title", as: "recap_title", type: "string" },
          { field: "blog_body", as: "recap_body", type: "string" },
          { field: "blog_word_count", as: "recap_word_count", type: "number" },
        ],
      ),

      // 5. AI generate SEO elements
      aiNode(
        "5",
        "AI generate SEO elements",
        1000,
        "You are an SEO specialist. Generate SEO elements for this webinar recap blog: meta title, meta description, target keywords, internal link suggestions, and schema markup recommendation. Respond JSON: {meta_title: string, meta_description: string, target_keywords: string[], slug: string, schema_type: string}",
        "Title: {recap_title}. Themes: {themes}. Content preview: {takeaways}",
        [
          { field: "meta_title", as: "seo_title", type: "string" },
          { field: "meta_description", as: "seo_description", type: "string" },
          { field: "target_keywords", as: "seo_keywords", type: "string" },
          { field: "slug", as: "seo_slug", type: "string" },
        ],
      ),

      // 6. AI generate social snippets
      aiNode(
        "6",
        "AI generate social media snippets",
        1240,
        "You are a social media manager. Create promotional snippets for the webinar recap: LinkedIn post, Twitter thread hooks (3 tweets), and Instagram caption. Reference key takeaways and link to the blog. Respond JSON: {linkedin_post: string, twitter_thread: string[], instagram_caption: string}",
        "Recap title: {recap_title}. Takeaways: {takeaways}. Quotes: {quotes}. Recording: {recording_url}",
        [
          { field: "linkedin_post", as: "social_linkedin", type: "string" },
          { field: "twitter_thread", as: "social_twitter", type: "string" },
          {
            field: "instagram_caption",
            as: "social_instagram",
            type: "string",
          },
        ],
      ),

      // 7. CMS + social queue publish
      n("7", "hitApi", "Publish to CMS and social queue", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{wordpress_domain}/wp-json/wp/v2/posts",
          headers: [
            { key: "Authorization", value: "Basic {wp_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{recap_title}" },
            { key: "content", value: "{recap_body}" },
            { key: "status", value: "draft" },
            { key: "slug", value: "{seo_slug}" },
            {
              key: "meta",
              value:
                '{"_yoast_wpseo_metadesc":"{seo_description}","_yoast_wpseo_focuskw":"{seo_keywords}"}',
            },
          ],
          variables: [
            { name: "id", customName: "wp_post_id", type: "number" },
            { name: "link", customName: "wp_post_url", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 195. OM68 PODCAST TO ARTICLE (8 nodes)
  // ===================================================================

  "OM68 Podcast to Article": {
    summary:
      "Triggers on episode upload, transcribes via Whisper, cleans up the transcript, extracts key moments, generates a full article, maps audio timestamps for embeds, optimizes for SEO, and publishes to CMS with guest notification email.",
    nodes: [
      trigger("Webhook: Podcast episode uploaded", "webhook_app", {
        source: "Podcast Host / S3",
        event: "episode.uploaded",
        howItStarts:
          "A new podcast episode audio file is uploaded and ready for article conversion",
        setup:
          "Connect your podcast hosting platform or S3 bucket to fire a webhook when a new episode is uploaded. Include audio URL, episode title, guest names, and show notes.",
        inputFields: [],
      }),

      // 2. Whisper transcription
      n("2", "hitApi", "Whisper API transcribe audio", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/audio/transcriptions",
          headers: [{ key: "Authorization", value: "Bearer {openai_key}" }],
          body: [
            { key: "model", value: "whisper-1" },
            { key: "file", value: "{audio_url}" },
            { key: "response_format", value: "verbose_json" },
            { key: "timestamp_granularities", value: '["segment"]' },
          ],
          variables: [
            { name: "text", customName: "raw_transcript", type: "string" },
            {
              name: "segments",
              customName: "transcript_segments",
              type: "string",
            },
            { name: "duration", customName: "audio_duration", type: "number" },
          ],
        },
      }),

      // 3. Transcript cleanup
      n("3", "runCode", "Clean and structure transcript", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Clean transcript and identify speaker segments
const raw = inputs.raw_transcript || '';
const segments = JSON.parse(inputs.transcript_segments || '[]');
const payload = JSON.parse(inputs.webhook_payload || '{}');

var cleaned = raw
  .replace(/\\b(um|uh|like|you know)\\b/gi, '')
  .replace(/\\s{2,}/g, ' ')
  .trim();

const episode_title = payload.title || payload.episode_title || '';
const guest_names = payload.guests || payload.guest_names || '';
const guest_email = payload.guest_email || '';
const show_notes = payload.show_notes || '';

return {
  cleaned_transcript: cleaned,
  episode_title: episode_title,
  guest_names: guest_names,
  guest_email: guest_email,
  show_notes: show_notes,
  word_count: cleaned.split(/\\s+/).filter(Boolean).length,
  segment_count: segments.length,
  timed_segments: JSON.stringify(segments.slice(0, 50))
};`,
          inputVariables: [
            { name: "raw_transcript", type: "string" },
            { name: "transcript_segments", type: "string" },
            { name: "webhook_payload", type: "string" },
          ],
          outputVariables: [
            { name: "cleaned_transcript", type: "string" },
            { name: "episode_title", type: "string" },
            { name: "guest_names", type: "string" },
            { name: "guest_email", type: "string" },
            { name: "show_notes", type: "string" },
            { name: "word_count", type: "number" },
            { name: "timed_segments", type: "string" },
          ],
        },
      }),

      // 4. AI extract key moments
      aiNode(
        "4",
        "AI extract key moments from episode",
        760,
        "You are a podcast content strategist. Identify the key moments, insights, stories, and quotable sections. Map them to approximate timestamps. Respond JSON: {key_moments: Array<{timestamp: string, topic: string, insight: string, is_quotable: boolean}>, main_topics: string[], guest_insights: string[], episode_summary: string}",
        "Episode: {episode_title}. Guest: {guest_names}. Show notes: {show_notes}. Transcript: {cleaned_transcript}",
        [
          { field: "key_moments", as: "key_moments", type: "string" },
          { field: "main_topics", as: "main_topics", type: "string" },
          { field: "guest_insights", as: "guest_insights", type: "string" },
          { field: "episode_summary", as: "episode_summary", type: "string" },
        ],
      ),

      // 5. AI generate article
      aiNode(
        "5",
        "AI generate full article from episode",
        1000,
        "You are a content writer. Transform this podcast episode into a comprehensive, standalone article. Use the key moments as structure, weave in guest quotes naturally, and make it valuable for readers who prefer reading over listening. Respond JSON: {article_title: string, article_body: string, article_word_count: number}",
        "Episode: {episode_title}. Guest: {guest_names}. Key moments: {key_moments}. Topics: {main_topics}. Insights: {guest_insights}. Summary: {episode_summary}",
        [
          { field: "article_title", as: "article_title", type: "string" },
          { field: "article_body", as: "article_body", type: "string" },
          {
            field: "article_word_count",
            as: "article_word_count",
            type: "number",
          },
        ],
      ),

      // 6. Audio embed mapping
      aiNode(
        "6",
        "AI map audio embed timestamps",
        1240,
        "You are a multimedia content specialist. Suggest audio embed placements throughout the article where listeners might want to hear the original audio. Map each to an approximate timestamp. Also generate an SEO-optimized meta description and keywords. Respond JSON: {audio_embeds: Array<{section: string, timestamp: string, description: string}>, meta_description: string, target_keywords: string[], schema_markup: string}",
        "Article: {article_body}. Key moments: {key_moments}. Timed segments: {timed_segments}",
        [
          { field: "audio_embeds", as: "audio_embeds", type: "string" },
          { field: "meta_description", as: "seo_meta", type: "string" },
          { field: "target_keywords", as: "seo_keywords", type: "string" },
        ],
      ),

      // 7. SEO optimization
      n("7", "hitApi", "Publish article to CMS", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{wordpress_domain}/wp-json/wp/v2/posts",
          headers: [
            { key: "Authorization", value: "Basic {wp_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{article_title}" },
            { key: "content", value: "{article_body}" },
            { key: "status", value: "draft" },
            {
              key: "meta",
              value:
                '{"_yoast_wpseo_metadesc":"{seo_meta}","_yoast_wpseo_focuskw":"{seo_keywords}"}',
            },
          ],
          variables: [
            { name: "id", customName: "wp_post_id", type: "number" },
            { name: "link", customName: "wp_post_url", type: "string" },
          ],
        },
      }),

      // 8. Guest notification email
      emailNode(
        "8",
        "Email guest: article published",
        1720,
        "{guest_email}",
        "Your podcast episode is now an article: {article_title}",
        "Hi {guest_names},\\n\\nThank you for joining us on the podcast! We've published an article based on our conversation:\\n\\nTitle: {article_title}\\nLink: {wp_post_url}\\n\\nFeel free to share it with your audience. We've included your key insights and quotes throughout.\\n\\nBest regards",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 196. OM69 SUPPORT TO KB ARTICLE (8 nodes)
  // ===================================================================

  "OM69 Support to KB Article": {
    summary:
      "Runs weekly to analyze support tickets, hydrates ticket data, clusters recurring issues with AI, checks existing KB coverage, drafts new articles, runs QA, routes through support content approval, and publishes with measurement tracking.",
    nodes: [
      trigger("Scheduled: Weekly support ticket analysis", "schedule", {
        schedule: "Every Monday at 8am",
        howItStarts:
          "Runs weekly to analyze the past week's support tickets and generate KB articles for recurring issues",
        setup:
          "Configure your support platform API credentials (Zendesk, Intercom, Freshdesk). Set the analysis window and minimum ticket threshold for KB article generation.",
        inputFields: [],
      }),

      // 2. Ticket hydration
      n("2", "hitApi", "Pull and hydrate recent support tickets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_domain}.zendesk.com/api/v2/search.json?query=type:ticket+created>7daysAgo&sort_by=created_at&sort_order=desc",
          headers: [{ key: "Authorization", value: "Basic {zendesk_auth}" }],
          body: [],
          variables: [
            { name: "results", customName: "ticket_data", type: "string" },
            { name: "count", customName: "ticket_count", type: "number" },
          ],
        },
      }),

      // 3. AI cluster recurring issues
      aiNode(
        "3",
        "AI cluster recurring support issues",
        520,
        "You are a support analytics expert. Analyze these support tickets and cluster them by recurring issue. Identify the top patterns, frequency, resolution steps, and KB article potential. Respond JSON: {clusters: Array<{issue: string, ticket_count: number, avg_resolution_time: string, resolution_steps: string[], kb_article_potential: string, sample_tickets: string[]}>, top_issues: string[], total_clustered: number}",
        "Tickets from past 7 days: {ticket_data}. Total count: {ticket_count}",
        [
          { field: "clusters", as: "issue_clusters", type: "string" },
          { field: "top_issues", as: "top_issues", type: "string" },
          { field: "total_clustered", as: "clustered_count", type: "number" },
        ],
      ),

      // 4. Existing KB check condition
      n(
        "4",
        "condition",
        "Check if KB articles already exist for top issues",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "clustered_count", operator: "greaterThan", value: "0" },
            ],
          },
        },
      ),

      // 5. AI draft KB articles
      aiNode(
        "5",
        "AI draft KB articles for uncovered issues",
        1000,
        "You are a technical writer specializing in knowledge base articles. For each uncovered issue cluster, write a complete KB article. Include: clear title, problem description, step-by-step solution, troubleshooting tips, and related articles section. Respond JSON: {articles: Array<{title: string, body: string, category: string, tags: string[], difficulty: string}>, articles_drafted: number}",
        "Issue clusters: {issue_clusters}. Top issues: {top_issues}",
        [
          { field: "articles", as: "kb_drafts", type: "string" },
          { field: "articles_drafted", as: "articles_drafted", type: "number" },
        ],
      ),

      // 6. QA pass
      aiNode(
        "6",
        "AI QA pass on KB drafts",
        1240,
        "You are a knowledge base quality auditor. Review each KB draft for: accuracy, completeness, clarity, searchability, and customer-friendliness. Score each and flag issues. Respond JSON: {qa_results: Array<{title: string, accuracy_score: number, clarity_score: number, overall_score: number, issues: string[]}>, all_pass: boolean, avg_score: number}",
        "KB drafts: {kb_drafts}",
        [
          { field: "qa_results", as: "qa_results", type: "string" },
          { field: "all_pass", as: "all_qa_pass", type: "string" },
          { field: "avg_score", as: "avg_qa_score", type: "number" },
        ],
      ),

      // 7. Support content approval gate
      n("7", "approvalGate", "Support content approval gate", 1480, {
        approvalGateDetails: {
          approvers: ["{support_lead_email}"],
          message:
            "Weekly KB article generation complete. {articles_drafted} articles drafted from {clustered_count} issue clusters. Average QA score: {avg_qa_score}/10.\\n\\nTop issues: {top_issues}\\n\\nPlease review and approve for publication.",
          timeoutHours: 72,
        },
      }),

      // 8. Publish + measure
      n("8", "hitApi", "Publish KB articles and log metrics", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://{zendesk_domain}.zendesk.com/api/v2/help_center/articles.json",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "article", value: "{kb_drafts}" }],
          variables: [
            { name: "article.id", customName: "kb_article_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 197. OM72 DOCS TO EXPLAINER (7 nodes)
  // ===================================================================

  "OM72 Docs to Explainer": {
    summary:
      "Takes technical documentation, extracts technical content, analyzes audience level with AI, rewrites as an accessible explainer, generates visual suggestions, runs an accuracy QA pass, and delivers to the docs review team.",
    nodes: [
      trigger(
        "Webhook: Documentation for explainer conversion",
        "webhook_app",
        {
          source: "Docs Platform / Engineering",
          event: "docs.explainer_requested",
          howItStarts:
            "Technical documentation is submitted to be rewritten as an accessible explainer article",
          setup:
            "POST the documentation content, source URL, target audience level (beginner/intermediate/advanced), and any specific focus areas to the workflow URL.",
          inputFields: [],
        },
      ),

      // 2. Technical content extraction
      aiNode(
        "2",
        "AI extract technical content elements",
        280,
        "You are a technical documentation analyst. Parse this documentation and extract: key concepts, technical terms, code examples, API references, architecture descriptions, and prerequisites. Respond JSON: {key_concepts: string[], technical_terms: Array<{term: string, definition: string}>, code_examples: string[], api_references: string[], prerequisites: string[], complexity_level: string, doc_type: string}",
        "{webhook_payload}",
        [
          { field: "key_concepts", as: "key_concepts", type: "string" },
          { field: "technical_terms", as: "technical_terms", type: "string" },
          { field: "code_examples", as: "code_examples", type: "string" },
          { field: "complexity_level", as: "complexity_level", type: "string" },
          { field: "doc_type", as: "doc_type", type: "string" },
        ],
      ),

      // 3. AI audience level analysis
      aiNode(
        "3",
        "AI analyze and set audience level",
        520,
        "You are an instructional designer. Based on the extracted content complexity and the target audience, determine the appropriate explanation depth, analogies to use, prerequisite knowledge to assume, and tone. Respond JSON: {audience_level: string, explanation_depth: string, analogies_to_use: string[], assumed_knowledge: string[], tone: string, simplification_strategy: string}",
        "Complexity: {complexity_level}. Doc type: {doc_type}. Key concepts: {key_concepts}. Technical terms: {technical_terms}",
        [
          { field: "audience_level", as: "target_level", type: "string" },
          {
            field: "explanation_depth",
            as: "explanation_depth",
            type: "string",
          },
          { field: "analogies_to_use", as: "analogies", type: "string" },
          {
            field: "simplification_strategy",
            as: "simplification_strategy",
            type: "string",
          },
          { field: "tone", as: "explainer_tone", type: "string" },
        ],
      ),

      // 4. AI rewrite as explainer
      aiNode(
        "4",
        "AI rewrite as accessible explainer",
        760,
        "You are a technical writer who excels at making complex topics simple. Rewrite the documentation as an engaging explainer article. Use the analogies suggested, explain terms in plain language, include practical examples, and maintain accuracy. Respond JSON: {explainer_title: string, explainer_body: string, word_count: number, glossary: Array<{term: string, plain_definition: string}>}",
        "Concepts: {key_concepts}. Terms: {technical_terms}. Code examples: {code_examples}. Audience: {target_level}. Depth: {explanation_depth}. Analogies: {analogies}. Tone: {explainer_tone}. Strategy: {simplification_strategy}",
        [
          { field: "explainer_title", as: "explainer_title", type: "string" },
          { field: "explainer_body", as: "explainer_body", type: "string" },
          { field: "word_count", as: "explainer_word_count", type: "number" },
          { field: "glossary", as: "glossary", type: "string" },
        ],
      ),

      // 5. AI generate visual suggestions
      aiNode(
        "5",
        "AI generate visual and diagram suggestions",
        1000,
        "You are a visual communication designer. Suggest diagrams, illustrations, and visual aids that would enhance the explainer article. For each, describe what it should show and why. Respond JSON: {visuals: Array<{type: string, description: string, placement: string, purpose: string}>, total_visuals: number}",
        "Explainer: {explainer_body}. Concepts: {key_concepts}. Audience: {target_level}",
        [
          { field: "visuals", as: "visual_suggestions", type: "string" },
          { field: "total_visuals", as: "visual_count", type: "number" },
        ],
      ),

      // 6. Accuracy QA condition
      n("6", "condition", "Accuracy QA: verify technical correctness", 1240, {
        conditionDetails: {
          conditions: [
            {
              field: "explainer_word_count",
              operator: "greaterThan",
              value: "200",
            },
          ],
        },
      }),

      // 7. Deliver to docs review
      slackNode(
        "7",
        "Deliver explainer to docs review team",
        1480,
        "📖 *Explainer Article Ready*\\nTitle: {explainer_title}\\nOriginal Complexity: {complexity_level} -> Target: {target_level}\\nWord Count: {explainer_word_count}\\nVisual Suggestions: {visual_count}\\n\\n*Glossary:*\\n{glossary}\\n\\n*Visual Suggestions:*\\n{visual_suggestions}\\n\\nFull article ready for technical accuracy review.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 198. OM73 FOUNDER NOTES TO POV (8 nodes)
  // ===================================================================

  "OM73 Founder Notes to POV": {
    summary:
      "Takes raw founder notes (text or audio), optionally transcribes via Whisper, extracts the core thesis with AI, finds supporting evidence via SerpAPI, drafts a POV article, checks voice consistency, formats for multiple channels, and sends for founder approval.",
    nodes: [
      trigger("Webhook: Founder notes submitted", "webhook_app", {
        source: "Slack / Email / Voice Memo",
        event: "founder_notes.submitted",
        howItStarts:
          "A founder submits rough notes, voice memos, or bullet points to be transformed into a polished POV article",
        setup:
          "POST the founder's notes (text or audio URL), their name, voice style preferences, and target publication channels to the workflow URL.",
        inputFields: [],
      }),

      // 2. Optional Whisper transcription (for audio)
      n(
        "2",
        "hitApi",
        "Whisper transcribe founder audio (if applicable)",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.openai.com/v1/audio/transcriptions",
            headers: [{ key: "Authorization", value: "Bearer {openai_key}" }],
            body: [
              { key: "model", value: "whisper-1" },
              { key: "file", value: "{audio_url}" },
              { key: "response_format", value: "text" },
            ],
            variables: [
              { name: "text", customName: "transcribed_notes", type: "string" },
            ],
          },
        },
      ),

      // 3. AI extract thesis
      aiNode(
        "3",
        "AI extract founder thesis and core argument",
        520,
        "You are a thought-leadership ghostwriter. Read these founder notes and extract: the core thesis, supporting arguments, contrarian positions, personal experiences referenced, and the 'so what' for the audience. Respond JSON: {core_thesis: string, supporting_arguments: string[], contrarian_positions: string[], personal_anecdotes: string[], audience_takeaway: string, conviction_level: string}",
        "Founder notes: {transcribed_notes}. Raw webhook data: {webhook_payload}",
        [
          { field: "core_thesis", as: "core_thesis", type: "string" },
          {
            field: "supporting_arguments",
            as: "supporting_args",
            type: "string",
          },
          {
            field: "contrarian_positions",
            as: "contrarian_positions",
            type: "string",
          },
          { field: "personal_anecdotes", as: "anecdotes", type: "string" },
          {
            field: "audience_takeaway",
            as: "audience_takeaway",
            type: "string",
          },
        ],
      ),

      // 4. SerpAPI evidence search
      n("4", "hitApi", "SerpAPI search for supporting evidence", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={core_thesis}+data+research+evidence&api_key={serpapi_key}&num=10",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "evidence_results",
              type: "string",
            },
          ],
        },
      }),

      // 5. AI draft POV article
      aiNode(
        "5",
        "AI draft POV article",
        1000,
        "You are a ghostwriter for a tech founder. Write a compelling, opinionated POV article that presents the founder's thesis with authority. Weave in data evidence, personal anecdotes, and contrarian perspectives. Make it provocative but substantiated. Respond JSON: {article_title: string, article_body: string, word_count: number, key_quotes: string[]}",
        "Thesis: {core_thesis}. Arguments: {supporting_args}. Contrarian: {contrarian_positions}. Anecdotes: {anecdotes}. Evidence: {evidence_results}. Takeaway: {audience_takeaway}",
        [
          { field: "article_title", as: "pov_title", type: "string" },
          { field: "article_body", as: "pov_body", type: "string" },
          { field: "word_count", as: "pov_word_count", type: "number" },
          { field: "key_quotes", as: "pov_quotes", type: "string" },
        ],
      ),

      // 6. Voice match condition
      n(
        "6",
        "condition",
        "Check voice consistency with founder's style",
        1240,
        {
          conditionDetails: {
            conditions: [
              {
                field: "pov_word_count",
                operator: "greaterThan",
                value: "300",
              },
            ],
          },
        },
      ),

      // 7. Channel formatting
      aiNode(
        "7",
        "AI format for target channels",
        1480,
        "You are a content distribution specialist. Adapt this POV article for LinkedIn (thought-leadership post), company blog (full article), and Twitter/X (thread). Maintain the founder's voice across all formats. Respond JSON: {linkedin_version: string, blog_version: string, twitter_thread: string[], email_newsletter_version: string}",
        "Title: {pov_title}. Body: {pov_body}. Key quotes: {pov_quotes}",
        [
          { field: "linkedin_version", as: "linkedin_pov", type: "string" },
          { field: "blog_version", as: "blog_pov", type: "string" },
          { field: "twitter_thread", as: "twitter_pov", type: "string" },
        ],
      ),

      // 8. Founder approval gate
      n("8", "approvalGate", "Founder approval gate", 1720, {
        approvalGateDetails: {
          approvers: ["{founder_email}"],
          message:
            "Your POV article is ready for review:\\n\\nTitle: {pov_title}\\nWord Count: {pov_word_count}\\nCore Thesis: {core_thesis}\\n\\nFormatted for: LinkedIn, Blog, Twitter/X\\n\\nPlease review for accuracy and voice. Approve to publish across all channels.",
          timeoutHours: 72,
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 199. PR NEWSJACKING BOT (8 nodes)
  // ===================================================================

  "PR Newsjacking Bot": {
    summary:
      "Scans news frequently via SerpAPI, scores opportunities with AI, filters by threshold, generates pitches, targets journalists via Hunter.io, sends pitches via SendGrid, tracks with Slack alerts, and logs feedback for iteration.",
    nodes: [
      trigger("Scheduled: Frequent news scan for newsjacking", "schedule", {
        schedule: "Every 2 hours during business hours",
        howItStarts:
          "Runs every 2 hours to scan breaking news for PR newsjacking opportunities",
        setup:
          "Configure target topics, industry keywords, competitor names, and journalist/media lists. Set opportunity scoring thresholds.",
        inputFields: [],
      }),

      // 2. SerpAPI news scan
      n("2", "hitApi", "SerpAPI scan breaking news", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={industry_keywords}+breaking+OR+announced+OR+launched&tbm=nws&tbs=qdr:h2&num=15&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "news_results",
              customName: "breaking_news",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "news_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI opportunity scoring
      aiNode(
        "3",
        "AI score newsjacking opportunities",
        520,
        "You are a PR strategist specializing in newsjacking. Evaluate each news item for newsjacking potential. Score on: relevance to our brand (1-10), timeliness window (1-10), differentiated angle available (1-10), media pickup potential (1-10). Respond JSON: {opportunities: Array<{headline: string, source: string, relevance: number, timeliness: number, angle_potential: number, media_potential: number, total_score: number, suggested_angle: string, urgency: string}>, top_opportunity: object, max_score: number}",
        "Breaking news: {breaking_news}. Our industry: SaaS/Marketing automation. Our expertise areas: customer engagement, email marketing, automation, data analytics.",
        [
          {
            field: "opportunities",
            as: "scored_opportunities",
            type: "string",
          },
          { field: "top_opportunity", as: "top_opportunity", type: "string" },
          { field: "max_score", as: "max_score", type: "number" },
        ],
      ),

      // 4. Threshold condition
      n("4", "condition", "Check if any opportunity meets threshold", 760, {
        conditionDetails: {
          conditions: [
            { field: "max_score", operator: "greaterThan", value: "25" },
          ],
        },
      }),

      // 5. AI generate pitch
      aiNode(
        "5",
        "AI generate newsjacking pitch",
        1000,
        "You are a PR writer. For the top newsjacking opportunity, craft a compelling media pitch. Include: subject line, pitch body, expert quote, data points, and a clear angle that connects the news to our expertise. Respond JSON: {pitch_subject: string, pitch_body: string, expert_quote: string, data_points: string[], pitch_angle: string, target_media_types: string[]}",
        "Top opportunity: {top_opportunity}. Our positioning: SaaS marketing automation leader with data-driven customer engagement expertise.",
        [
          { field: "pitch_subject", as: "pitch_subject", type: "string" },
          { field: "pitch_body", as: "pitch_body", type: "string" },
          { field: "pitch_angle", as: "pitch_angle", type: "string" },
          { field: "target_media_types", as: "target_media", type: "string" },
        ],
      ),

      // 6. Hunter.io journalist targeting
      n("6", "hitApi", "Hunter.io find target journalists", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/domain-search?domain={target_publication_domain}&api_key={hunter_key}&type=personal&department=editorial",
          headers: [],
          body: [],
          variables: [
            {
              name: "data.emails",
              customName: "journalist_contacts",
              type: "string",
            },
            {
              name: "meta.results",
              customName: "contacts_found",
              type: "number",
            },
          ],
        },
      }),

      // 7. SendGrid pitch delivery
      n("7", "hitApi", "SendGrid deliver pitch to journalists", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.sendgrid.com/v3/mail/send",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "personalizations",
              value: '[{"to":[{"email":"{journalist_email}"}]}]',
            },
            { key: "from", value: '{"email":"{pr_email}","name":"{pr_name}"}' },
            { key: "subject", value: "{pitch_subject}" },
            {
              key: "content",
              value: '[{"type":"text/html","value":"{pitch_body}"}]',
            },
            {
              key: "tracking_settings",
              value: '{"open_tracking":{"enable":true}}',
            },
          ],
          variables: [
            {
              name: "statusCode",
              customName: "pitch_send_status",
              type: "number",
            },
          ],
        },
      }),

      // 8. Tracking + feedback log
      slackNode(
        "8",
        "Slack alert and tracking log",
        1720,
        "📰 *Newsjacking Pitch Sent!*\\nAngle: {pitch_angle}\\nSubject: {pitch_subject}\\nJournalists Contacted: {contacts_found}\\nOpportunity Score: {max_score}/40\\n\\n*Next Steps:*\\n- Monitor for replies\\n- Track opens via SendGrid\\n- Follow up in 24h if no response",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 200. QUOTE-CARD COPY GENERATOR (6 nodes)
  // ===================================================================

  "Quote-Card Copy Generator": {
    summary:
      "Ingests quotes, extracts quotable elements, generates AI visual-ready variants, creates design direction notes, inserts into a content queue spreadsheet, and notifies the team.",
    nodes: [
      trigger("Webhook: Quotes for card generation", "webhook_app", {
        source: "Content Team / CMS",
        event: "quotes.submitted",
        howItStarts:
          "A set of quotes (from interviews, podcasts, customer testimonials) is submitted for visual quote-card generation",
        setup:
          "POST the raw quotes, speaker names, context, and brand color preferences to the workflow URL.",
        inputFields: [],
      }),

      // 2. Extract quotable elements
      aiNode(
        "2",
        "AI extract quotable elements",
        280,
        "You are a visual content specialist. Analyze these quotes and extract the most quotable segments — short, punchy, visually impactful phrases. For each, note the emotional tone and visual energy. Respond JSON: {quotable_segments: Array<{original: string, extracted_quote: string, speaker: string, tone: string, visual_energy: string, max_chars: number}>, total_segments: number}",
        "{webhook_payload}",
        [
          {
            field: "quotable_segments",
            as: "quotable_segments",
            type: "string",
          },
          { field: "total_segments", as: "segment_count", type: "number" },
        ],
      ),

      // 3. AI generate variants
      aiNode(
        "3",
        "AI generate quote-card text variants",
        520,
        "You are a visual copywriter. For each quotable segment, create 3 variants optimized for different card formats: (1) Ultra-short for square cards (under 15 words), (2) Medium for story/portrait cards (under 30 words), (3) Full context for wide cards (under 50 words). Respond JSON: {variants: Array<{original: string, speaker: string, ultra_short: string, medium: string, full_context: string, hashtag: string}>, total_variants: number}",
        "Quotable segments: {quotable_segments}",
        [
          { field: "variants", as: "quote_variants", type: "string" },
          { field: "total_variants", as: "variant_count", type: "number" },
        ],
      ),

      // 4. AI generate design direction
      aiNode(
        "4",
        "AI generate design direction for each card",
        760,
        "You are a graphic design director. For each quote variant set, suggest design direction: background style (gradient, photo, pattern, solid), typography mood (bold serif, modern sans, handwritten), color palette suggestion, and layout composition. Respond JSON: {design_directions: Array<{quote: string, background_style: string, typography: string, color_suggestion: string, layout: string, mood_board_keywords: string[]}>}",
        "Quote variants: {quote_variants}",
        [
          {
            field: "design_directions",
            as: "design_directions",
            type: "string",
          },
        ],
      ),

      // 5. Insert into content queue Sheets
      n("5", "hitApi", "Insert into content queue Google Sheets", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/QuoteCards!A:F:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "values", value: "{quote_variants}" }],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "cards_queued",
              type: "number",
            },
          ],
        },
      }),

      // 6. Team Slack notification
      slackNode(
        "6",
        "Notify team: quote cards queued",
        1240,
        "🎴 *Quote Cards Ready for Design*\\nQuotable Segments: {segment_count}\\nVariants Generated: {variant_count}\\nCards Queued: {cards_queued}\\n\\nDesign directions included. Check the content queue in Sheets.",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 201. REDDIT/HN/X TREND DETECTOR (8 nodes)
  // ===================================================================

  "Reddit HN X Trend Detector": {
    summary:
      "Scans Reddit, Hacker News, and X for trends via SerpAPI, filters signals with code, clusters themes with AI, scores brand relevance, generates response options, routes by urgency, sends Slack alerts for fast-moving trends, and logs for creative handoff.",
    nodes: [
      trigger("Scheduled: Social trend scan", "schedule", {
        schedule: "Every 4 hours",
        howItStarts:
          "Runs periodically to scan Reddit, Hacker News, and X/Twitter for emerging trends in your industry",
        setup:
          "Configure target subreddits, HN categories, X search terms, and brand relevance keywords.",
        inputFields: [],
      }),

      // 2. SerpAPI trend scan
      n("2", "hitApi", "SerpAPI scan social platforms for trends", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q=site:reddit.com+OR+site:news.ycombinator.com+{industry_keywords}&tbs=qdr:d&num=20&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "trend_results",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "total_signals",
              type: "number",
            },
          ],
        },
      }),

      // 3. Signal filtering
      n("3", "runCode", "Filter and normalize trend signals", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Filter and normalize trend signals from social platforms
const results = JSON.parse(inputs.trend_results || '[]');

const filtered = results
  .filter(function(r) {
    // Filter out low-quality signals
    var title = (r.title || '').toLowerCase();
    return !title.includes('hiring') && !title.includes('show hn') && title.length > 20;
  })
  .map(function(r) {
    var platform = 'other';
    if (r.link && r.link.includes('reddit.com')) platform = 'reddit';
    else if (r.link && r.link.includes('ycombinator')) platform = 'hackernews';
    else if (r.link && r.link.includes('twitter.com') || r.link && r.link.includes('x.com')) platform = 'x';

    return {
      title: r.title,
      url: r.link,
      snippet: r.snippet || '',
      platform: platform,
      engagement_signal: r.rich_snippet ? 'high' : 'normal'
    };
  });

return {
  filtered_signals: JSON.stringify(filtered),
  signal_count: filtered.length,
  reddit_count: filtered.filter(function(f) { return f.platform === 'reddit'; }).length,
  hn_count: filtered.filter(function(f) { return f.platform === 'hackernews'; }).length
};`,
          inputVariables: [{ name: "trend_results", type: "string" }],
          outputVariables: [
            { name: "filtered_signals", type: "string" },
            { name: "signal_count", type: "number" },
            { name: "reddit_count", type: "number" },
            { name: "hn_count", type: "number" },
          ],
        },
      }),

      // 4. AI cluster themes
      aiNode(
        "4",
        "AI cluster trend themes",
        760,
        "You are a social listening analyst. Cluster these social signals into themes. Identify emerging narratives, sentiment shifts, and conversation momentum. Respond JSON: {themes: Array<{theme: string, signal_count: number, platforms: string[], sentiment: string, momentum: string, sample_titles: string[]}>, emerging_themes: string[], declining_themes: string[], total_themes: number}",
        "Signals: {filtered_signals}. Signal count: {signal_count}",
        [
          { field: "themes", as: "clustered_themes", type: "string" },
          { field: "emerging_themes", as: "emerging_themes", type: "string" },
          { field: "total_themes", as: "theme_count", type: "number" },
        ],
      ),

      // 5. AI brand relevance scoring
      aiNode(
        "5",
        "AI score brand relevance and response options",
        1000,
        "You are a brand strategist. Score each theme for brand relevance and generate response options. For each theme: relevance score (1-10), urgency (low/medium/high/critical), and 2-3 response options (content idea, community response, product tie-in). Respond JSON: {scored_themes: Array<{theme: string, relevance_score: number, urgency: string, response_options: string[]}>, highest_urgency: string, actionable_count: number}",
        "Themes: {clustered_themes}. Emerging: {emerging_themes}. Our brand: SaaS marketing automation platform focused on customer engagement.",
        [
          { field: "scored_themes", as: "scored_themes", type: "string" },
          { field: "highest_urgency", as: "highest_urgency", type: "string" },
          { field: "actionable_count", as: "actionable_count", type: "number" },
        ],
      ),

      // 6. Urgency condition
      n("6", "condition", "Check for high-urgency trends", 1240, {
        conditionDetails: {
          conditions: [
            { field: "highest_urgency", operator: "equals", value: "critical" },
          ],
        },
      }),

      // 7. Slack alert for fast trends (True)
      slackNode(
        "7",
        "Slack alert: fast-moving trend detected",
        1480,
        "🔥 *URGENT: Fast-Moving Trend Detected*\\nUrgency: {highest_urgency}\\nActionable Themes: {actionable_count}/{theme_count}\\n\\n*Emerging Themes:*\\n{emerging_themes}\\n\\n*Top Scored Themes:*\\n{scored_themes}\\n\\n*Signals:* Reddit: {reddit_count}, HN: {hn_count}, Total: {signal_count}",
      ),

      // 8. Creative handoff logging (both branches)
      n(
        "8",
        "hitApi",
        "Log trends for creative handoff",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/TrendLog!A:F:append?valueInputOption=USER_ENTERED",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "values",
                value:
                  "[['{highest_urgency}','{theme_count}','{actionable_count}','{emerging_themes}','{scored_themes}','{signal_count}']]",
              },
            ],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "trends_logged",
                type: "number",
              },
            ],
          },
        },
        500,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 202. SUBJECT LINE A/B GENERATOR (7 nodes)
  // ===================================================================

  "Subject Line AB Generator": {
    summary:
      "Ingests a campaign setup, pulls past performance data from Sheets, generates AI subject line variants with scoring, selects the best test pair, configures the A/B test in SendGrid, and logs results for learning.",
    nodes: [
      trigger(
        "Webhook: Email campaign setup for subject line testing",
        "webhook_app",
        {
          source: "Email Marketing / Campaign Manager",
          event: "campaign.subject_test_requested",
          howItStarts:
            "An email campaign is set up and needs A/B subject line variants generated and configured",
          setup:
            "POST the campaign details including email content preview, audience segment, campaign goal, and any constraints to the workflow URL.",
          inputFields: [],
        },
      ),

      // 2. Pull past performance data
      n("2", "hitApi", "Pull past subject line performance from Sheets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/SubjectLineHistory!A:G",
          headers: [{ key: "Authorization", value: "Bearer {google_token}" }],
          body: [],
          variables: [
            {
              name: "values",
              customName: "historical_performance",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI generate variants
      aiNode(
        "3",
        "AI generate subject line variants",
        520,
        "You are an email marketing copywriter and A/B testing expert. Given the campaign context and historical performance data, generate 8-10 subject line variants using different psychological approaches: curiosity, urgency, benefit-led, question, how-to, number/list, personalized, social proof, fear-of-missing-out, and contrarian. Respond JSON: {variants: Array<{subject_line: string, approach: string, predicted_open_rate: number, rationale: string, emoji_version: string}>, patterns_from_history: string[]}",
        "{webhook_payload}. Historical performance: {historical_performance}",
        [
          { field: "variants", as: "subject_variants", type: "string" },
          {
            field: "patterns_from_history",
            as: "historical_patterns",
            type: "string",
          },
        ],
      ),

      // 4. AI scoring and explanation
      aiNode(
        "4",
        "AI score and explain each variant",
        760,
        "You are an email deliverability and engagement expert. Score each subject line variant on: deliverability risk (1-10, 10=safe), click appeal (1-10), inbox standout (1-10), brand alignment (1-10), and spam trigger risk (1-10, 10=no risk). Recommend the best A/B pair. Respond JSON: {scored_variants: Array<{subject_line: string, deliverability: number, click_appeal: number, standout: number, brand_fit: number, spam_safety: number, total: number}>, recommended_pair: {variant_a: string, variant_b: string, pair_rationale: string}, winning_prediction: string}",
        "Variants: {subject_variants}. Historical patterns: {historical_patterns}",
        [
          { field: "scored_variants", as: "scored_variants", type: "string" },
          { field: "recommended_pair", as: "recommended_pair", type: "string" },
          {
            field: "winning_prediction",
            as: "winning_prediction",
            type: "string",
          },
        ],
      ),

      // 5. Test pair selection
      n("5", "runCode", "Select and configure A/B test pair", 1000, {
        codeDetails: {
          language: "javascript",
          code: `// Extract the recommended A/B pair and configure test parameters
const pair = JSON.parse(inputs.recommended_pair || '{}');
const variant_a = pair.variant_a || 'Variant A';
const variant_b = pair.variant_b || 'Variant B';
const rationale = pair.pair_rationale || '';

return {
  test_variant_a: variant_a,
  test_variant_b: variant_b,
  test_rationale: rationale,
  test_split: '50/50',
  test_duration_hours: 4,
  winner_metric: 'open_rate',
  sample_size_per_variant: 1000
};`,
          inputVariables: [{ name: "recommended_pair", type: "string" }],
          outputVariables: [
            { name: "test_variant_a", type: "string" },
            { name: "test_variant_b", type: "string" },
            { name: "test_rationale", type: "string" },
            { name: "test_split", type: "string" },
            { name: "test_duration_hours", type: "number" },
            { name: "winner_metric", type: "string" },
            { name: "sample_size_per_variant", type: "number" },
          ],
        },
      }),

      // 6. SendGrid A/B configuration
      n("6", "hitApi", "Configure SendGrid A/B test", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.sendgrid.com/v3/marketing/singlesends",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "name",
              value: "AB Test: {test_variant_a} vs {test_variant_b}",
            },
            {
              key: "ab_test",
              value:
                '{"ab_test_type":"subject","ab_test_percentage":50,"ab_test_duration_hours":{test_duration_hours},"winning_metric":"{winner_metric}","subject_a":"{test_variant_a}","subject_b":"{test_variant_b}"}',
            },
          ],
          variables: [
            { name: "id", customName: "sendgrid_campaign_id", type: "string" },
          ],
        },
      }),

      // 7. Result logging
      n("7", "hitApi", "Log A/B test to performance tracker", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/SubjectLineHistory!A:G:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                "[['{test_variant_a}','{test_variant_b}','{test_rationale}','{winning_prediction}','{sendgrid_campaign_id}','{test_split}','{test_duration_hours}']]",
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "test_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 203. TREND-TO-POST IDEA GENERATOR (7 nodes)
  // ===================================================================

  "Trend to Post Idea Generator": {
    summary:
      "Ingests a trending topic, analyzes brand connection with AI, generates post ideas, suggests visuals, scores timeliness, inserts into the content calendar, and alerts the social team.",
    nodes: [
      trigger("Webhook: Trending topic for content ideation", "webhook_app", {
        source: "Trend Detection System / Manual",
        event: "trend.detected",
        howItStarts:
          "A trending topic is identified and needs rapid content ideation for social media",
        setup:
          "POST the trending topic details including trend description, platform of origin, current velocity, and any brand relevance notes to the workflow URL.",
        inputFields: [],
      }),

      // 2. AI brand connection analysis
      aiNode(
        "2",
        "AI analyze brand connection to trend",
        280,
        "You are a brand strategist. Analyze how this trending topic connects to our brand, products, expertise, and audience. Identify authentic connection points — never force-fit. Respond JSON: {trend_topic: string, brand_connection_score: number, connection_angles: string[], authentic_ties: string[], risks: string[], audience_interest_level: string, recommended_approach: string}",
        "{webhook_payload}",
        [
          { field: "trend_topic", as: "trend_topic", type: "string" },
          {
            field: "brand_connection_score",
            as: "brand_connection",
            type: "number",
          },
          {
            field: "connection_angles",
            as: "connection_angles",
            type: "string",
          },
          { field: "authentic_ties", as: "authentic_ties", type: "string" },
          { field: "risks", as: "trend_risks", type: "string" },
          {
            field: "recommended_approach",
            as: "recommended_approach",
            type: "string",
          },
        ],
      ),

      // 3. AI generate post ideas
      aiNode(
        "3",
        "AI generate social post ideas",
        520,
        "You are a social media creative director. Generate 5-8 post ideas that authentically connect the trend to our brand. For each, specify platform, format (carousel, reel, tweet, story, poll), hook, and full caption direction. Respond JSON: {post_ideas: Array<{title: string, platform: string, format: string, hook: string, caption_direction: string, connection_angle: string, estimated_engagement: string}>, total_ideas: number}",
        "Trend: {trend_topic}. Connection angles: {connection_angles}. Authentic ties: {authentic_ties}. Approach: {recommended_approach}. Risks to avoid: {trend_risks}",
        [
          { field: "post_ideas", as: "post_ideas", type: "string" },
          { field: "total_ideas", as: "idea_count", type: "number" },
        ],
      ),

      // 4. AI visual suggestions
      aiNode(
        "4",
        "AI suggest visuals for each post idea",
        760,
        "You are a visual content strategist. For each post idea, suggest the visual treatment: image style, video concept, carousel layout, or graphic design direction. Include color mood, reference styles, and production complexity. Respond JSON: {visual_suggestions: Array<{post_title: string, visual_type: string, visual_description: string, production_complexity: string, reference_style: string}>}",
        "Post ideas: {post_ideas}. Trend: {trend_topic}",
        [
          {
            field: "visual_suggestions",
            as: "visual_suggestions",
            type: "string",
          },
        ],
      ),

      // 5. AI timeliness scoring
      aiNode(
        "5",
        "AI score timeliness and prioritize",
        1000,
        "You are a social media timing expert. Score each post idea for timeliness: how long is the trend window, when should each post go live, and what is the optimal posting sequence. Respond JSON: {timed_posts: Array<{title: string, urgency: string, post_by: string, trend_window_hours: number, priority: number}>, posting_sequence: string[], overall_timeliness: string}",
        "Post ideas: {post_ideas}. Trend: {trend_topic}. Brand connection: {brand_connection}/10",
        [
          { field: "timed_posts", as: "timed_posts", type: "string" },
          { field: "posting_sequence", as: "posting_sequence", type: "string" },
          {
            field: "overall_timeliness",
            as: "overall_timeliness",
            type: "string",
          },
        ],
      ),

      // 6. Content calendar Sheets insertion
      n("6", "hitApi", "Insert post ideas into content calendar", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/ContentCalendar!A:G:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "values", value: "{timed_posts}" }],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "posts_scheduled",
              type: "number",
            },
          ],
        },
      }),

      // 7. Social team Slack alert
      slackNode(
        "7",
        "Slack alert: trend-based posts ready",
        1480,
        "⚡ *Trend Content Ideas Ready*\\nTrend: {trend_topic}\\nBrand Connection: {brand_connection}/10\\nIdeas Generated: {idea_count}\\nTimeliness: {overall_timeliness}\\n\\n*Posting Sequence:*\\n{posting_sequence}\\n\\n*Risks:*\\n{trend_risks}\\n\\nIdeas added to content calendar. Act fast on time-sensitive posts!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 204. UGC SCRIPT PACK GENERATOR (7 nodes)
  // ===================================================================

  "UGC Script Pack Generator": {
    summary:
      "Ingests a UGC campaign brief, researches trending formats with AI, generates a complete script pack with multiple concepts, adds creator notes, generates hook and thumbnail concepts, assembles in Google Docs, and notifies the influencer team.",
    nodes: [
      trigger("Webhook: UGC campaign brief submitted", "webhook_app", {
        source: "Influencer Team / Campaign Manager",
        event: "ugc_brief.submitted",
        howItStarts:
          "A UGC campaign brief is submitted and needs a complete script pack generated for creators",
        setup:
          "POST the UGC brief including product details, target audience, campaign goals, platform targets (TikTok, Reels, Shorts), budget tier, and any brand guidelines to the workflow URL.",
        inputFields: [],
      }),

      // 2. AI research trending formats
      aiNode(
        "2",
        "AI research trending UGC formats",
        280,
        "You are a UGC and short-form video specialist. Analyze current trending formats, hooks, transitions, and storytelling structures on TikTok/Reels/Shorts. Identify what is performing well right now for the given product category. Respond JSON: {trending_formats: Array<{format_name: string, description: string, platform: string, avg_engagement: string, example_structure: string}>, trending_hooks: string[], trending_transitions: string[], product_category: string, campaign_goal: string, target_platforms: string[]}",
        "{webhook_payload}",
        [
          { field: "trending_formats", as: "trending_formats", type: "string" },
          { field: "trending_hooks", as: "trending_hooks", type: "string" },
          { field: "product_category", as: "product_category", type: "string" },
          { field: "campaign_goal", as: "campaign_goal", type: "string" },
          { field: "target_platforms", as: "target_platforms", type: "string" },
        ],
      ),

      // 3. AI generate script pack
      aiNode(
        "3",
        "AI generate UGC script pack",
        520,
        "You are a UGC scriptwriter. Generate 5-7 complete UGC video scripts. Each should use a different trending format. Include: concept name, hook (first 3 seconds), script with timestamps, B-roll notes, audio/music suggestions, and estimated duration. Respond JSON: {scripts: Array<{concept_name: string, format: string, platform: string, duration_seconds: number, hook: string, full_script: string, broll_notes: string, audio_suggestion: string}>, total_scripts: number}",
        "Product category: {product_category}. Goal: {campaign_goal}. Platforms: {target_platforms}. Trending formats: {trending_formats}. Trending hooks: {trending_hooks}",
        [
          { field: "scripts", as: "script_pack", type: "string" },
          { field: "total_scripts", as: "script_count", type: "number" },
        ],
      ),

      // 4. AI add creator notes
      aiNode(
        "4",
        "AI add creator guidance notes",
        760,
        "You are a UGC program manager. For each script, add creator guidance notes: performance tips, do's and don'ts, lighting/framing suggestions, authenticity tips, common mistakes to avoid, and post-production notes. Respond JSON: {creator_notes: Array<{concept_name: string, performance_tips: string[], dos: string[], donts: string[], lighting_framing: string, authenticity_tip: string, post_production: string}>, general_guidelines: string}",
        "Scripts: {script_pack}. Product: {product_category}. Platforms: {target_platforms}",
        [
          { field: "creator_notes", as: "creator_notes", type: "string" },
          {
            field: "general_guidelines",
            as: "general_guidelines",
            type: "string",
          },
        ],
      ),

      // 5. AI generate hook and thumbnail concepts
      aiNode(
        "5",
        "AI generate hook and thumbnail concepts",
        1000,
        "You are a short-form video growth hacker. For each script, generate 3 thumbnail/cover image concepts and 2 alternative hooks. Thumbnails should be scroll-stopping. Alternative hooks test different psychological angles. Respond JSON: {concepts: Array<{concept_name: string, thumbnail_ideas: Array<{description: string, text_overlay: string, visual_style: string}>, alt_hooks: string[]}>, total_thumbnails: number}",
        "Scripts: {script_pack}. Product: {product_category}. Trending hooks: {trending_hooks}",
        [
          { field: "concepts", as: "hook_thumbnail_concepts", type: "string" },
          { field: "total_thumbnails", as: "thumbnail_count", type: "number" },
        ],
      ),

      // 6. Assemble in Google Docs
      n("6", "hitApi", "Assemble script pack in Google Docs", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://docs.googleapis.com/v1/documents",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "title",
              value: "UGC Script Pack: {product_category} - {campaign_goal}",
            },
            {
              key: "body",
              value:
                '{"content":[{"paragraph":{"elements":[{"textRun":{"content":"UGC Script Pack\\n\\nProduct: {product_category}\\nGoal: {campaign_goal}\\nPlatforms: {target_platforms}\\nScripts: {script_count}\\n\\nGeneral Guidelines:\\n{general_guidelines}\\n\\n--- SCRIPTS ---\\n{script_pack}\\n\\n--- CREATOR NOTES ---\\n{creator_notes}\\n\\n--- HOOKS & THUMBNAILS ---\\n{hook_thumbnail_concepts}"}}]}}]}',
            },
          ],
          variables: [
            { name: "documentId", customName: "doc_id", type: "string" },
            { name: "title", customName: "doc_title", type: "string" },
          ],
        },
      }),

      // 7. Influencer team notification
      slackNode(
        "7",
        "Notify influencer team: script pack ready",
        1480,
        "🎬 *UGC Script Pack Ready*\\nProduct: {product_category}\\nCampaign Goal: {campaign_goal}\\nPlatforms: {target_platforms}\\nScripts: {script_count}\\nThumbnail Concepts: {thumbnail_count}\\n\\n*General Guidelines:*\\n{general_guidelines}\\n\\nFull script pack with creator notes in Google Docs: {doc_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },
};
